/**
 * AdminDNPPage — moderation queue for the community Do-Not-Pay database.
 *
 * Every submission at /dnp lands in `db.dnp_reports` as `status: pending`.
 * Admins approve/reject here; only `approved` reports show up in public
 * search results at /dnp. Rejects and deletes are also tracked for audit.
 *
 * Feeds off:
 *   GET  /api/admin/dnp/queue?status=pending
 *   GET  /api/admin/dnp/stats
 *   POST /api/admin/dnp/approve
 *   POST /api/admin/dnp/reject
 *   POST /api/admin/dnp/delete
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { CheckCircle, XCircle, Trash, Warning, ClockCounterClockwise, ShieldCheck, ShareNetwork, X as XIcon, Copy, VideoCamera, RedditLogo, Files, Monitor, Play, Pause, ArrowClockwise, FilmSlate, Download, TiktokLogo, LinkSimple, PaperPlaneTilt, BellRinging } from "@phosphor-icons/react";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import client from "../lib/api";
import { WinWallReelCard } from "../components/admin/WinWallReelCard";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS_TABS = [
  { code: "pending", label: "Pending review", tone: "amber" },
  { code: "approved", label: "Approved", tone: "emerald" },
  { code: "rejected", label: "Rejected", tone: "neutral" },
];

const TONE_STYLES = {
  amber: "border-amber-400/50 bg-amber-400/[0.06] text-amber-300",
  emerald: "border-emerald-400/50 bg-emerald-400/[0.06] text-emerald-300",
  neutral: "border-neutral-600 bg-neutral-800/40 text-neutral-300",
};

/** Resolve the best video URL for a promo result.
 *  Prefers Cloudinary CDN (durable + fast) when available,
 *  falls back to the local /api/tiktok-videos/ URL. */
function videoUrl(data) {
  if (!data) return null;
  if (data.cdn_url) return data.cdn_url;
  if (data.mp4_url) return `${API_BASE}${data.mp4_url}`;
  return null;
}

/** Force-download variant of videoUrl:
 *  - Cloudinary: inject `fl_attachment` so the browser saves instead of streaming.
 *  - Local: fall through untouched (backend already serves as attachment). */
function downloadUrl(data) {
  const url = videoUrl(data);
  if (!url) return null;
  if (url.includes("res.cloudinary.com") && url.includes("/upload/")) {
    return url.replace("/upload/", "/upload/fl_attachment/");
  }
  return url;
}

/** Auto Angle Tags — remember the utm_content of any ad link the founder
 *  copies so the spend logger can pre-fill/offer it (never skip tagging). */
function rememberAngle(url) {
  try {
    const qs = String(url || "").includes("?") ? String(url).split("?")[1] : "";
    const angle = new URLSearchParams(qs).get("utm_content");
    if (!angle) return;
    const cur = JSON.parse(localStorage.getItem("fp_recent_angles") || "[]")
      .filter((a) => a !== angle);
    cur.unshift(angle);
    localStorage.setItem("fp_recent_angles", JSON.stringify(cur.slice(0, 8)));
  } catch { /* noop */ }
}

/** Build a paste-ready TikTok caption for a given topic.
 *  Structure: hook → link in bio → hashtag bundle. */
function promoCaption(topicLabel, utmSuffix) {
  const link = `https://freightpilotai.ai/${utmSuffix || ""}`;
  const hook = topicLabel
    ? `${topicLabel} — the SaaS built by an owner-op for owner-ops.`
    : `FreightPilotAI — built by an owner-op for owner-ops.`;
  return (
    `${hook}\n` +
    `Link in bio → ${link}\n\n` +
    `#trucking #owneroperator #dispatch #truckertok #smallfleet ` +
    `#truckingbusiness #truckdispatcher #truckingsoftware`
  );
}


function fmt(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}

export default function AdminDNPPage() {
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("pending");
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioningId, setActioningId] = useState(null);
  const [digestInfo, setDigestInfo] = useState(null);
  const [digestSending, setDigestSending] = useState(false);
  const [openRate, setOpenRate] = useState(null);
  const [redditReportId, setRedditReportId] = useState(null);
  const [redditDrafts, setRedditDrafts] = useState(null);
  const [redditLoading, setRedditLoading] = useState(false);
  const [tiktokReportId, setTiktokReportId] = useState(null);
  const [tiktokScript, setTiktokScript] = useState(null);
  const [tiktokLoading, setTiktokLoading] = useState(false);
  const [redditScanning, setRedditScanning] = useState(false);
  const [redditScanResult, setRedditScanResult] = useState(null);
  const [batchMode, setBatchMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [batchExporting, setBatchExporting] = useState(false);

  const loadStats = async () => {
    try {
      const r = await client.get("/admin/dnp/stats");
      setStats(r.data);
    } catch { /* silent */ }
  };

  const loadDigestInfo = async () => {
    try {
      const r = await client.get("/admin/dnp/digest/preview");
      setDigestInfo(r.data);
    } catch { /* silent */ }
  };

  const loadOpenRate = async () => {
    try {
      const r = await client.get("/admin/dnp/weekly-digest/open-rate", { params: { weeks: 8 } });
      setOpenRate(r.data);
    } catch { /* silent — chart just doesn't render */ }
  };

  const sendDigestNow = async () => {
    setDigestSending(true);
    try {
      const r = await client.post("/admin/dnp/digest/send-now");
      const body = r.data || {};
      if (body.posted) {
        toast.success(`Slack digest fired — ${body.pending_count} report(s)`);
      } else if (body.reason === "no_pending") {
        toast.info("Queue is empty — nothing to send");
      } else if (body.reason === "no_webhook") {
        toast.error("SLACK_DNP_WEBHOOK_URL is not set on the backend");
      } else {
        toast.error(`Digest skipped: ${body.reason || "unknown"}`);
      }
      await loadDigestInfo();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Digest send failed");
    } finally {
      setDigestSending(false);
    }
  };

  const openReddit = async (id) => {
    setRedditReportId(id);
    setRedditDrafts(null);
    setRedditLoading(true);
    try {
      const r = await client.get(`/admin/dnp/reddit-draft/${id}`);
      setRedditDrafts(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load Reddit drafts");
      setRedditReportId(null);
    } finally {
      setRedditLoading(false);
    }
  };

  const closeReddit = () => {
    setRedditReportId(null);
    setRedditDrafts(null);
  };

  const openTiktok = async (id) => {
    setTiktokReportId(id);
    setTiktokScript(null);
    setTiktokLoading(true);
    try {
      const r = await client.get(`/admin/dnp/tiktok-script/${id}`);
      setTiktokScript(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't generate TikTok script");
      setTiktokReportId(null);
    } finally {
      setTiktokLoading(false);
    }
  };

  const closeTiktok = () => {
    setTiktokReportId(null);
    setTiktokScript(null);
  };

  const runRedditScan = async () => {
    setRedditScanning(true);
    setRedditScanResult(null);
    try {
      const r = await client.post("/admin/dnp/reddit-scan/run-now");
      setRedditScanResult(r.data);
      const body = r.data || {};
      if (body.imported > 0) {
        const src = [];
        if ((body.news_imported ?? 0) > 0) src.push(`${body.news_imported} from Google News`);
        const redditCount = body.imported - (body.news_imported ?? 0);
        if (redditCount > 0) src.push(`${redditCount} from Reddit`);
        toast.success(`${body.imported} new pending report(s) imported${src.length ? ` — ${src.join(", ")}` : ""}`);
        loadQueue(tab);
        loadStats();
      } else if ((body.news_scanned ?? 0) > 0 || body.scanned_posts > 0) {
        toast.info(
          `Scanned ${body.news_scanned ?? 0} news items${
            body.reddit_reachable === false
              ? " (Reddit blocked — expected)"
              : ` + ${body.scanned_posts} Reddit posts`
          } — nothing new matched`
        );
      } else {
        toast.info("Nothing new right now");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reddit scan failed");
    } finally {
      setRedditScanning(false);
    }
  };

  const loadQueue = async (status) => {
    setLoading(true);
    try {
      const r = await client.get(`/admin/dnp/queue?status=${status}&limit=100`);
      setReports(r.data.results || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load DNP queue");
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStats(); loadDigestInfo(); loadOpenRate(); }, []);
  useEffect(() => { loadQueue(tab); }, [tab]);

  const approve = async (id) => {
    setActioningId(id);
    try {
      await client.post("/admin/dnp/approve", { report_id: id });
      toast.success("Report approved — now public");
      await Promise.all([loadStats(), loadQueue(tab)]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally {
      setActioningId(null);
    }
  };

  const reject = async (id) => {
    const note = window.prompt("Reason for rejection (optional — for internal audit):", "");
    if (note === null) return; // user cancelled
    setActioningId(id);
    try {
      await client.post("/admin/dnp/reject", { report_id: id, reviewer_note: note });
      toast.success("Report rejected — hidden from public");
      await Promise.all([loadStats(), loadQueue(tab)]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally {
      setActioningId(null);
    }
  };

  const hardDelete = async (id) => {
    if (!window.confirm("Permanently delete this report? Cannot be undone.")) return;
    setActioningId(id);
    try {
      await client.post("/admin/dnp/delete", { report_id: id });
      toast.success("Report deleted");
      await Promise.all([loadStats(), loadQueue(tab)]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setActioningId(null);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 10) {
        toast.error("Batch export is capped at 10 reports per file");
        return prev;
      }
      return [...prev, id];
    });
  };

  const clearSelection = () => setSelectedIds([]);

  const exitBatchMode = () => {
    setBatchMode(false);
    setSelectedIds([]);
  };

  const runBatchExport = async () => {
    if (selectedIds.length === 0) {
      toast.error("Select at least 1 report to export");
      return;
    }
    setBatchExporting(true);
    try {
      const res = await client.post(
        "/admin/dnp/tiktok-batch-export",
        { report_ids: selectedIds },
        { responseType: "blob" },
      );
      const blob = new Blob([res.data], { type: "text/markdown;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const day = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.href = url;
      a.download = `freightpilotai-tiktok-batch-${day}-${selectedIds.length}scripts.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`${selectedIds.length} script(s) exported as markdown`);
      exitBatchMode();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Batch export failed";
      toast.error(String(msg));
    } finally {
      setBatchExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white p-6 lg:p-10" data-testid="admin-dnp-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-2 flex items-center gap-2">
              <ShieldCheck size={14} weight="fill" /> DNP Moderation
            </div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Do-Not-Pay report queue</h1>
            <p className="text-neutral-400 text-sm mt-2 max-w-2xl">
              Every user-submitted broker report lands here as <b>pending</b>. Approve to make it visible in public search at <code className="text-[#FF5A1F]">/dnp</code>. Reject to hide it. Delete for spam.
            </p>
          </div>

          {/* Stat tiles */}
          {stats && (
            <div className="grid grid-cols-3 gap-2" data-testid="admin-dnp-stats">
              <StatTile label="Pending" value={stats.pending} tone="amber" testid="admin-dnp-stat-pending" />
              <StatTile label="Approved" value={stats.approved} tone="emerald" testid="admin-dnp-stat-approved" />
              <StatTile label="Rejected" value={stats.rejected} tone="neutral" testid="admin-dnp-stat-rejected" />
            </div>
          )}
        </div>

        {/* Slack digest status */}
        {digestInfo && (
          <div className="mb-6 rounded-xl border border-white/10 bg-white/[0.02] p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="admin-dnp-digest-card">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${digestInfo.webhook_configured ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>
              <ShieldCheck size={20} weight="duotone" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-white flex items-center gap-2 flex-wrap" data-testid="admin-dnp-digest-status">
                Daily Slack digest
                <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${digestInfo.webhook_configured
                  ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"
                  : "bg-amber-500/15 text-amber-300 border border-amber-500/40"}`}>
                  {digestInfo.webhook_configured ? "Wired" : "Not wired"}
                </span>
              </div>
              <div className="text-[12px] text-neutral-400 mt-0.5 leading-snug">
                {digestInfo.webhook_configured
                  ? <>Fires at 9am ET daily. Silent when queue is empty. Currently <b className="text-white">{digestInfo.pending_count}</b> pending report{digestInfo.pending_count === 1 ? "" : "s"}.</>
                  : <>Set <code className="text-[#FF5A1F]">SLACK_DNP_WEBHOOK_URL</code> in the backend env, then <a href="https://api.slack.com/messaging/webhooks" target="_blank" rel="noopener" className="text-[#FF5A1F] hover:underline">create an Incoming Webhook</a> for your <code>#dnp-moderation</code> channel.</>
                }
              </div>
            </div>
            <button type="button" onClick={sendDigestNow} disabled={digestSending}
              data-testid="admin-dnp-digest-send-btn"
              className="shrink-0 inline-flex items-center gap-1.5 rounded-md px-3.5 py-2 text-xs font-semibold bg-white/10 hover:bg-[#FF5A1F] hover:text-black text-white transition-colors disabled:opacity-50 border border-white/10">
              {digestSending ? "Sending…" : "Send test digest now"}
            </button>
          </div>
        )}

        {/* TikTok Connect panel — OAuth v2 + Content Posting API (Sandbox) */}
        <TikTokConnectPanel />
        <TikTokAutoPostPanel />

        {/* 1-Click AI Video Generator — pick + render today's TikTok in one click */}
        <OneClickTikTokCard />
        <ReelLibraryCard />
        <ChampionCameosCard />

        {/* Weekly-digest open-rate chart */}
        <WeeklyDigestOpenRateCard openRate={openRate} />

        {/* Reddit auto-fetch card */}
        <div className="mb-6 rounded-xl border border-white/10 bg-white/[0.02] p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="admin-dnp-reddit-scan-card">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${redditScanResult && redditScanResult.reddit_reachable === false ? "bg-amber-500/15 text-amber-400" : "bg-orange-500/15 text-orange-400"}`}>
            <RedditLogo size={22} weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white flex items-center gap-2 flex-wrap" data-testid="admin-dnp-reddit-scan-status">
              Public feed auto-fetch
              <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${redditScanResult
                ? (redditScanResult.reddit_reachable === false
                    ? "bg-amber-500/15 text-amber-300 border border-amber-500/40"
                    : "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40")
                : "bg-white/5 text-white/50 border border-white/10"}`}>
                {redditScanResult
                  ? (redditScanResult.reddit_reachable === false ? "Blocked" : "Reachable")
                  : "Not scanned yet"}
              </span>
            </div>
            <div className="text-[12px] text-neutral-400 mt-0.5 leading-snug">
              {redditScanResult && redditScanResult.reddit_reachable === false ? (
                <>Reddit rejects requests from this datacenter IP, but the <b className="text-white">Google News feed</b> keeps importing — last run scanned <b className="text-white">{redditScanResult.news_scanned ?? 0}</b> news item{(redditScanResult.news_scanned ?? 0) === 1 ? "" : "s"} and imported <b className="text-emerald-300">{redditScanResult.news_imported ?? 0}</b> pending report{(redditScanResult.news_imported ?? 0) === 1 ? "" : "s"}.</>
              ) : redditScanResult ? (
                <>Last run scanned <b className="text-white">{redditScanResult.scanned_posts}</b> Reddit post{redditScanResult.scanned_posts === 1 ? "" : "s"} + <b className="text-white">{redditScanResult.news_scanned ?? 0}</b> news item{(redditScanResult.news_scanned ?? 0) === 1 ? "" : "s"} — imported <b className="text-emerald-300">{redditScanResult.imported}</b> new pending report{redditScanResult.imported === 1 ? "" : "s"}.</>
              ) : (
                <>Auto-fetches broker fraud mentions from Google News + Reddit every 6 hours. Every match lands in Pending for your review.</>
              )}
            </div>
          </div>
          <button type="button" onClick={runRedditScan} disabled={redditScanning}
            data-testid="admin-dnp-reddit-scan-btn"
            className="shrink-0 inline-flex items-center gap-1.5 rounded-md px-3.5 py-2 text-xs font-semibold bg-white/10 hover:bg-[#FF5A1F] hover:text-black text-white transition-colors disabled:opacity-50 border border-white/10">
            {redditScanning ? "Scanning…" : "Run scan now"}
          </button>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap items-center gap-2 justify-between border-b border-white/10 mb-6">
          <div className="flex gap-1 overflow-x-auto max-w-full -mx-1 px-1">
            {STATUS_TABS.map((t) => (
              <button
                key={t.code}
                onClick={() => setTab(t.code)}
                data-testid={`admin-dnp-tab-${t.code}`}
                className={`shrink-0 px-3 sm:px-5 py-3 text-sm font-semibold transition-colors border-b-2 flex items-center gap-2 ${
                  tab === t.code ? "border-[#FF5A1F] text-white" : "border-transparent text-white/50 hover:text-white/80"
                }`}
              >
                {t.code === "pending" && <ClockCounterClockwise size={14} />}
                {t.code === "approved" && <CheckCircle size={14} />}
                {t.code === "rejected" && <XCircle size={14} />}
                {t.label}
                {stats && (
                  <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded font-mono ${TONE_STYLES[t.tone]}`}>
                    {stats[t.code]}
                  </span>
                )}
              </button>
            ))}
          </div>
          {tab === "approved" && (
            <div className="pb-2 flex items-center gap-2" data-testid="admin-dnp-batch-toolbar">
              {batchMode ? (
                <>
                  <span className="text-[11px] text-white/50 font-mono">
                    <b className="text-[#FF5A1F]">{selectedIds.length}</b>/10 selected
                  </span>
                  <button
                    type="button"
                    onClick={clearSelection}
                    disabled={selectedIds.length === 0}
                    data-testid="admin-dnp-batch-clear"
                    className="text-xs text-white/60 hover:text-white disabled:opacity-40 font-semibold px-2 py-1"
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={runBatchExport}
                    disabled={batchExporting || selectedIds.length === 0}
                    data-testid="admin-dnp-batch-export-btn"
                    className="inline-flex items-center gap-1.5 rounded-md px-3.5 py-2 text-xs font-bold bg-[#FF5A1F] hover:bg-[#FF7038] text-black transition-colors disabled:opacity-50"
                  >
                    <Files size={12} weight="bold" />
                    {batchExporting ? "Building…" : "Export .md file"}
                  </button>
                  <button
                    type="button"
                    onClick={exitBatchMode}
                    disabled={batchExporting}
                    data-testid="admin-dnp-batch-exit"
                    className="text-xs text-white/60 hover:text-white font-semibold px-2 py-1"
                  >
                    Exit
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setBatchMode(true)}
                  data-testid="admin-dnp-batch-mode-btn"
                  className="inline-flex items-center gap-1.5 rounded-md px-3.5 py-2 text-xs font-semibold bg-white/10 hover:bg-[#FF5A1F] hover:text-black text-white transition-colors border border-white/10"
                >
                  <Files size={12} weight="bold" /> Batch TikTok export
                </button>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        {loading ? (
          <div className="text-center text-neutral-400 py-16" data-testid="admin-dnp-loading">Loading…</div>
        ) : reports.length === 0 ? (
          <div className="rounded-xl border border-white/10 bg-white/5 p-12 text-center" data-testid="admin-dnp-empty">
            <Warning size={40} className="mx-auto mb-3 text-neutral-500" weight="thin" />
            <div className="text-neutral-300 font-semibold mb-1">No {tab} reports</div>
            <div className="text-neutral-500 text-sm">
              {tab === "pending"
                ? "You're all caught up — no reports awaiting review."
                : `Nothing in the ${tab} bucket yet.`}
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-dnp-list">
            {reports.map((r) => (
              <ReportRow
                key={r.id}
                r={r}
                onApprove={() => approve(r.id)}
                onReject={() => reject(r.id)}
                onDelete={() => hardDelete(r.id)}
                onOpenReddit={() => openReddit(r.id)}
                onOpenTiktok={() => openTiktok(r.id)}
                actioning={actioningId === r.id}
                currentTab={tab}
                batchMode={batchMode && tab === "approved"}
                selected={selectedIds.includes(r.id)}
                onToggleSelect={() => toggleSelect(r.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Reddit draft modal */}
      {redditReportId && (
        <RedditDraftModal
          drafts={redditDrafts}
          loading={redditLoading}
          onClose={closeReddit}
        />
      )}

      {/* TikTok script modal */}
      {tiktokReportId && (
        <TikTokScriptModal
          data={tiktokScript}
          loading={tiktokLoading}
          onClose={closeTiktok}
          reportId={tiktokReportId}
        />
      )}
    </div>
  );
}

/* Weekly DNP digest open-rate card — reads from the /admin/dnp/weekly-digest/open-rate
   endpoint (powered by the Resend email.opened webhook). Renders a
   compact 8-week bar chart with per-week open-rate percentages.
   Hides itself when there's no data yet (fresh account state). */
function WeeklyDigestOpenRateCard({ openRate }) {
  if (!openRate) return null;
  const weeks = openRate.weeks || [];
  if (weeks.length === 0 || (openRate.summary?.total_sent ?? 0) === 0) return null;

  const summary = openRate.summary || {};
  const avg = summary.avg_open_rate_pct ?? 0;
  const maxPct = Math.max(...weeks.map((w) => w.open_rate_pct || 0), 1);

  return (
    <div
      className="mb-6 rounded-xl border border-white/10 bg-white/[0.02] p-5"
      data-testid="admin-dnp-open-rate-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-1">
            Weekly digest habit
          </div>
          <div className="text-xl font-black tracking-tight text-white">
            {avg.toFixed(1)}% <span className="text-white/50 text-base font-semibold">avg open rate · last {weeks.length} weeks</span>
          </div>
          <div className="text-[12px] text-white/50 mt-1">
            <b className="text-white">{(summary.total_opened ?? 0).toLocaleString()}</b> carriers opened / <b className="text-white">{(summary.total_sent ?? 0).toLocaleString()}</b> total sent. Tracked via Resend <code className="text-[#FF5A1F]">email.opened</code> webhook.
          </div>
        </div>
        <div
          className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full ${avg >= 25 ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40" : avg >= 15 ? "bg-amber-500/15 text-amber-300 border border-amber-500/40" : "bg-red-500/15 text-red-300 border border-red-500/40"}`}
          data-testid="admin-dnp-open-rate-badge"
        >
          {avg >= 25 ? "Healthy" : avg >= 15 ? "Building habit" : "Needs work"}
        </div>
      </div>

      <div className="flex items-stretch gap-2 h-32" data-testid="admin-dnp-open-rate-chart">
        {weeks.map((w) => {
          const pct = Number(w.open_rate_pct || 0);
          const heightPct = maxPct > 0 ? (pct / maxPct) * 100 : 0;
          return (
            <div
              key={w.week}
              className="flex-1 flex flex-col items-center gap-1 group"
              data-testid={`admin-dnp-open-rate-bar-${w.week}`}
              title={`${w.week}: ${w.opened}/${w.sent} opened (${pct}%)`}
            >
              <div className="text-[10px] font-mono text-white/60 tabular-nums">
                {pct.toFixed(0)}%
              </div>
              <div className="w-full flex-1 flex items-end">
                <div
                  className={`w-full rounded-t transition-all ${pct >= 25 ? "bg-emerald-500/70 group-hover:bg-emerald-500" : pct >= 15 ? "bg-amber-500/70 group-hover:bg-amber-500" : "bg-red-500/60 group-hover:bg-red-500"}`}
                  style={{ height: `${Math.max(heightPct, 2)}%` }}
                />
              </div>
              <div className="text-[9px] font-mono text-white/40 truncate w-full text-center">
                {w.week.replace(/^\d+-W/, "W")}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}



function StatTile({ label, value, tone, testid }) {
  return (
    <div className={`rounded-lg border px-3 py-2 min-w-[80px] ${TONE_STYLES[tone]}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest opacity-70 font-bold">{label}</div>
      <div className="text-2xl font-black text-white">{value ?? 0}</div>
    </div>
  );
}

function ReportRow({ r, onApprove, onReject, onDelete, onOpenReddit, onOpenTiktok, actioning, currentTab, batchMode = false, selected = false, onToggleSelect }) {
  return (
    <div
      className={`rounded-xl border p-5 transition-colors ${
        batchMode
          ? (selected
              ? "border-[#FF5A1F]/60 bg-[#FF5A1F]/[0.06]"
              : "border-white/10 bg-white/5 hover:border-white/20 cursor-pointer")
          : "border-white/10 bg-white/5 hover:border-white/20"
      }`}
      data-testid={`admin-dnp-row-${r.id}`}
      onClick={batchMode ? onToggleSelect : undefined}
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          {batchMode && (
            <input
              type="checkbox"
              checked={selected}
              onChange={onToggleSelect}
              onClick={(e) => e.stopPropagation()}
              data-testid={`admin-dnp-batch-checkbox-${r.id}`}
              className="mt-1 w-5 h-5 rounded border-white/30 accent-[#FF5A1F] cursor-pointer shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <span className="text-[9px] uppercase tracking-widest bg-red-500/20 border border-red-500/40 text-red-100 font-bold px-2 py-0.5 rounded">
                {r.incident_type_label}
              </span>
              {r.incident_date && <span className="text-[10px] text-white/40">on {r.incident_date}</span>}
              {r.amount_lost && <span className="text-[10px] text-red-300 font-mono">Lost ${r.amount_lost.toLocaleString()}</span>}
            </div>
            <div className="text-lg font-bold text-white">{r.broker_name}</div>
            <div className="text-sm text-white/50 flex flex-wrap gap-x-3">
              {r.broker_mc && <span>{r.broker_mc}</span>}
              {r.broker_dot && <span>DOT {r.broker_dot}</span>}
            </div>
          </div>
        </div>

        {!batchMode && currentTab === "pending" && (
          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            <button
              onClick={onApprove}
              disabled={actioning}
              className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-black font-bold rounded-lg px-4 py-2 text-sm transition-colors flex items-center gap-1.5"
              data-testid={`admin-dnp-approve-${r.id}`}
            >
              <CheckCircle size={14} weight="bold" /> Approve
            </button>
            <button
              onClick={onReject}
              disabled={actioning}
              className="bg-white/5 hover:bg-white/10 disabled:opacity-40 text-white/80 border border-white/10 font-semibold rounded-lg px-4 py-2 text-sm transition-colors flex items-center gap-1.5"
              data-testid={`admin-dnp-reject-${r.id}`}
            >
              <XCircle size={14} weight="bold" /> Reject
            </button>
          </div>
        )}

        {!batchMode && currentTab === "approved" && (
          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            <button
              onClick={() => onOpenReddit(r.id)}
              disabled={actioning}
              className="bg-[#FF5A1F] hover:bg-[#FF7038] disabled:opacity-40 text-black font-bold rounded-lg px-4 py-2 text-sm transition-colors flex items-center gap-1.5"
              data-testid={`admin-dnp-reddit-${r.id}`}
              title="Copy formatted Reddit posts to warn other carriers"
            >
              <ShareNetwork size={14} weight="bold" /> Reddit
            </button>
            <button
              onClick={() => onOpenTiktok(r.id)}
              disabled={actioning}
              className="bg-white/10 hover:bg-white/20 disabled:opacity-40 text-white border border-white/10 font-bold rounded-lg px-4 py-2 text-sm transition-colors flex items-center gap-1.5"
              data-testid={`admin-dnp-tiktok-${r.id}`}
              title="Generate a 15-second TikTok script"
            >
              <VideoCamera size={14} weight="bold" /> TikTok
            </button>
            <button
              onClick={onReject}
              disabled={actioning}
              className="bg-amber-500/20 hover:bg-amber-500/30 disabled:opacity-40 text-amber-200 border border-amber-500/30 font-semibold rounded-lg px-4 py-2 text-sm transition-colors flex items-center gap-1.5"
              data-testid={`admin-dnp-unapprove-${r.id}`}
            >
              <XCircle size={14} weight="bold" /> Take down
            </button>
          </div>
        )}

        {!batchMode && currentTab === "rejected" && (
          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            <button
              onClick={onApprove}
              disabled={actioning}
              className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-black font-bold rounded-lg px-4 py-2 text-sm transition-colors flex items-center gap-1.5"
              data-testid={`admin-dnp-restore-${r.id}`}
            >
              <CheckCircle size={14} weight="bold" /> Restore
            </button>
            <button
              onClick={onDelete}
              disabled={actioning}
              className="bg-red-500/20 hover:bg-red-500/30 disabled:opacity-40 text-red-200 border border-red-500/30 font-semibold rounded-lg px-3 py-2 text-sm transition-colors flex items-center gap-1.5"
              data-testid={`admin-dnp-delete-${r.id}`}
            >
              <Trash size={14} weight="bold" />
            </button>
          </div>
        )}
      </div>

      <p className="text-sm text-white/70 leading-snug whitespace-pre-wrap">{r.description}</p>
      {r.article_excerpt && (
        <div
          className="mt-2 rounded-md border border-white/10 bg-white/[0.03] p-3 text-[12px] text-white/60 leading-relaxed"
          data-testid={`admin-dnp-article-excerpt-${r.id}`}
        >
          <div className="text-[9px] uppercase tracking-widest text-sky-300 font-bold mb-1">Article excerpt</div>
          {r.article_excerpt.slice(0, 600)}
          {r.article_excerpt.length > 600 ? "…" : ""}
        </div>
      )}
      {(r.resolved_url || r.source_url) && (
        <a
          href={r.resolved_url || r.source_url}
          target="_blank"
          rel="noreferrer"
          className="mt-2 inline-flex items-center gap-1 text-[11px] text-[#FF9764] hover:text-[#FF5A1F] font-semibold transition-colors"
          data-testid={`admin-dnp-source-link-${r.id}`}
        >
          Read source article ↗
        </a>
      )}

      <div className="mt-4 pt-3 border-t border-white/10 grid sm:grid-cols-3 gap-3 text-[11px] text-white/50">
        <div>
          <div className="text-[9px] uppercase tracking-widest text-white/40 font-bold mb-0.5">Reporter</div>
          {r.reporter_carrier_name && <div className="text-white/70">{r.reporter_carrier_name}</div>}
          {r.reporter_email && <div className="font-mono text-[10px] break-all">{r.reporter_email}</div>}
          {!r.reporter_carrier_name && !r.reporter_email && <div className="text-white/30">Anonymous</div>}
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-widest text-white/40 font-bold mb-0.5">Submitted</div>
          <div>{fmt(r.created_at)}</div>
          <div className="font-mono text-[10px] text-white/30 mt-0.5">IP: {r.reporter_ip || "—"}</div>
        </div>
        {r.reviewed_at && (
          <div>
            <div className="text-[9px] uppercase tracking-widest text-white/40 font-bold mb-0.5">Reviewed</div>
            <div>{fmt(r.reviewed_at)}</div>
            <div className="text-[10px] text-white/40">by {r.reviewed_by || "—"}</div>
            {r.reviewer_note && <div className="text-[10px] text-white/60 italic mt-0.5">"{r.reviewer_note}"</div>}
          </div>
        )}
        {r.upvotes > 0 && (
          <div>
            <div className="text-[9px] uppercase tracking-widest text-white/40 font-bold mb-0.5">Public upvotes</div>
            <div className="text-white/80 font-bold">{r.upvotes}</div>
          </div>
        )}
      </div>
    </div>
  );
}


/* ─────────────────── Reddit draft modal ─────────────────── */
function RedditDraftModal({ drafts, loading, onClose }) {
  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Clipboard blocked — select text manually");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="admin-dnp-reddit-modal">
      <div className="max-w-3xl w-full max-h-[90vh] overflow-y-auto rounded-2xl border-2 border-[#FF5A1F]/30 bg-neutral-950 p-6"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-1">
              Share on Reddit
            </div>
            <h2 className="text-2xl font-black tracking-tight text-white">
              Ready-to-post drafts
            </h2>
            <p className="text-sm text-white/50 mt-1 max-w-xl leading-snug">
              Copy either draft below and post from your personal Reddit account.
              Do NOT auto-post — Reddit's spam filters penalize new / bot-flavored
              accounts. Manual posting from a warmed-up account gets 10× the visibility.
            </p>
          </div>
          <button type="button" onClick={onClose}
            className="w-9 h-9 rounded-lg hover:bg-white/10 text-white/60 flex items-center justify-center transition-colors"
            data-testid="admin-dnp-reddit-close">
            <XIcon size={18} weight="bold" />
          </button>
        </div>

        {loading || !drafts ? (
          <div className="py-14 text-center text-white/50 text-sm">Loading drafts…</div>
        ) : (
          <div className="space-y-5" data-testid="admin-dnp-reddit-drafts">
            {Object.entries(drafts.drafts).map(([key, draft]) => (
              <div key={key} className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid={`admin-dnp-reddit-draft-${key}`}>
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-bold">
                      {draft.subreddit}
                    </span>
                    <span className="text-[10px] text-white/40">→ pastes below</span>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <a href={draft.submit_url} target="_blank" rel="noopener noreferrer"
                      data-testid={`admin-dnp-reddit-submit-${key}`}
                      className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-bold bg-[#FF5A1F] hover:bg-[#FF7038] text-black transition-colors">
                      <ShareNetwork size={12} weight="bold" /> Open Reddit prefilled
                    </a>
                    <button type="button" onClick={() => copy(draft.title, "Title")}
                      data-testid={`admin-dnp-reddit-copy-title-${key}`}
                      className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-semibold bg-white/5 hover:bg-white/10 text-white/80 transition-colors border border-white/10">
                      <Copy size={12} weight="bold" /> Copy title
                    </button>
                    <button type="button" onClick={() => copy(draft.body_markdown, "Body")}
                      data-testid={`admin-dnp-reddit-copy-body-${key}`}
                      className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-semibold bg-white/5 hover:bg-white/10 text-white/80 transition-colors border border-white/10">
                      <Copy size={12} weight="bold" /> Copy body
                    </button>
                  </div>
                </div>
                <div className="text-[13px] font-bold text-white leading-snug mb-2">
                  {draft.title}
                </div>
                <pre className="text-[12px] leading-relaxed text-white/70 whitespace-pre-wrap font-mono bg-black/40 rounded-md p-3 border border-white/5 max-h-64 overflow-y-auto">
{draft.body_markdown}
                </pre>
              </div>
            ))}
            <div className="text-[11px] text-white/40 leading-relaxed border-t border-white/10 pt-4">
              <b className="text-white/60">Posting tips:</b> Use a personal account (not a
              brand one) with karma ≥ 100 and account age ≥ 30 days. Space posts out
              across subreddits by 2+ hours. If a broker disputes, edit the report at
              /admin/dnp — the public entry updates immediately.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────── TikTok script modal ─────────────────── */
function TikTokScriptModal({ data, loading, onClose, reportId }) {
  const [teleprompterOn, setTeleprompterOn] = useState(false);
  const [videoState, setVideoState] = useState({ status: "idle", url: null, duration: null });
  const [videoVoice, setVideoVoice] = useState("onyx");
  const [videoDuration, setVideoDuration] = useState(15);
  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Clipboard blocked — select text manually");
    }
  };

  const copyAll = () => {
    if (!data || !data.script) return;
    const s = data.script;
    const cards = (s.cards || [])
      .map((c, i) => `Card ${i + 1} (${c.seconds}s)\nOn-screen: ${c.on_screen_text}\nSpoken: ${c.spoken}`)
      .join("\n\n");
    const bRoll = (s.b_roll_tips || []).map((t) => `• ${t}`).join("\n");
    const script = [
      `🎬 TikTok script — ${data.broker || "DNP report"} (${data.identifiers || ""})`,
      "",
      `Hook: ${s.hook || ""}`,
      "",
      cards,
      "",
      `CTA: ${s.cta || ""}`,
      "",
      `Hashtags: ${(s.hashtags || []).join(" ")}`,
      "",
      bRoll ? `B-roll tips:\n${bRoll}` : "",
    ].filter(Boolean).join("\n");
    copy(script, "Full script");
  };

  const generateVideo = async (force = false) => {
    if (!reportId) return;
    setVideoState({ status: "generating", url: null, duration: null });
    try {
      const res = await client.post(
        `/admin/dnp/tiktok-video/${reportId}?voice=${encodeURIComponent(videoVoice)}&duration_sec=${videoDuration}${force ? "&force=true" : ""}`,
        null,
        { timeout: 180_000 },
      );
      setVideoState({
        status: "ready",
        url: res.data?.mp4_url,
        duration: res.data?.duration_sec,
        cached: res.data?.cached,
      });
      toast.success(res.data?.cached ? "Loaded cached video" : "Video generated");
    } catch (e) {
      setVideoState({ status: "error", url: null, duration: null });
      const msg = e?.response?.data?.detail || e?.message || "Video generation failed";
      toast.error(String(msg));
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="admin-dnp-tiktok-modal">
      <div className="max-w-3xl w-full max-h-[90vh] overflow-y-auto rounded-2xl border-2 border-[#FF5A1F]/30 bg-neutral-950 p-6"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4 gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-1">
              15-second TikTok script
            </div>
            <h2 className="text-2xl font-black tracking-tight text-white truncate">
              {loading || !data ? "Generating…" : data.broker}
            </h2>
            {data && (
              <p className="text-xs text-white/50 mt-1 font-mono">
                {data.identifiers} · {data.script?.generator === "fallback_template" ? "Template fallback (LLM unreachable)" : "AI-drafted, edit before you post"}
              </p>
            )}
          </div>
          <button type="button" onClick={onClose}
            className="w-9 h-9 rounded-lg hover:bg-white/10 text-white/60 flex items-center justify-center transition-colors shrink-0"
            data-testid="admin-dnp-tiktok-close">
            <XIcon size={18} weight="bold" />
          </button>
        </div>

        {loading || !data ? (
          <div className="py-14 text-center text-white/50 text-sm">
            Composing a 15-second short-form script…
            <div className="text-[11px] text-white/30 mt-2">This uses Claude Sonnet — usually 3-6 seconds.</div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-dnp-tiktok-script">
            <div className="flex justify-end gap-2">
              <button type="button"
                onClick={() => setTeleprompterOn(true)}
                data-testid="admin-dnp-tiktok-teleprompter-btn"
                className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-bold bg-white/10 hover:bg-white/20 text-white border border-white/10 transition-colors">
                <Monitor size={12} weight="bold" /> Teleprompter mode
              </button>
              <button type="button" onClick={copyAll}
                data-testid="admin-dnp-tiktok-copy-all"
                className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-bold bg-[#FF5A1F] hover:bg-[#FF7038] text-black transition-colors">
                <Copy size={12} weight="bold" /> Copy full script
              </button>
            </div>

            {/* AI Video Generator */}
            <div
              className="rounded-xl border-2 border-emerald-500/40 bg-emerald-500/[0.06] p-4"
              data-testid="admin-dnp-tiktok-video-panel"
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold mb-1">
                    🎥 AI Video Generator
                  </div>
                  <div className="text-sm font-bold text-white">
                    One-click {videoDuration}-sec TikTok MP4 · voiceover + captions
                  </div>
                  <div className="text-[11px] text-white/60 mt-1 leading-snug">
                    Generates a ready-to-upload 1080×1920 vertical MP4.
                    Cost ≈ ${videoDuration === 60 ? "0.10" : videoDuration === 30 ? "0.05" : "0.03"} per video ·
                    takes ~{videoDuration === 60 ? "90" : videoDuration === 30 ? "50" : "30"}s.
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div
                    className="inline-flex rounded-md bg-black/40 border border-white/10 p-0.5"
                    data-testid="admin-dnp-tiktok-video-duration"
                    role="radiogroup"
                    aria-label="Video duration"
                  >
                    {[15, 30, 60].map((d) => (
                      <button
                        key={d}
                        type="button"
                        role="radio"
                        aria-checked={videoDuration === d}
                        onClick={() => setVideoDuration(d)}
                        disabled={videoState.status === "generating"}
                        data-testid={`admin-dnp-tiktok-video-duration-${d}`}
                        className={`px-2.5 py-1 text-[11px] font-bold rounded transition-colors font-mono disabled:opacity-50 ${
                          videoDuration === d
                            ? "bg-emerald-500 text-black"
                            : "text-white/70 hover:text-white"
                        }`}
                      >
                        {d}s
                      </button>
                    ))}
                  </div>
                  <select
                    value={videoVoice}
                    onChange={(e) => setVideoVoice(e.target.value)}
                    disabled={videoState.status === "generating"}
                    data-testid="admin-dnp-tiktok-video-voice"
                    className="text-xs bg-black/40 border border-white/10 rounded px-2 py-1.5 text-white font-mono"
                  >
                    <option value="onyx">onyx · deep + trucker</option>
                    <option value="echo">echo · smooth</option>
                    <option value="nova">nova · energetic</option>
                    <option value="ash">ash · articulate</option>
                    <option value="sage">sage · wise</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => generateVideo(false)}
                    disabled={videoState.status === "generating"}
                    data-testid="admin-dnp-tiktok-video-generate"
                    className="inline-flex items-center gap-1.5 rounded-md px-3.5 py-2 text-xs font-bold bg-emerald-500 hover:bg-emerald-400 text-black transition-colors disabled:opacity-50"
                  >
                    <FilmSlate size={12} weight="bold" />
                    {videoState.status === "generating"
                      ? "Rendering…"
                      : videoState.status === "ready"
                        ? "Regenerate"
                        : "Generate video"}
                  </button>
                </div>
              </div>

              {videoState.status === "generating" && (
                <div
                  className="mt-3 text-[12px] text-white/70 flex items-center gap-2"
                  data-testid="admin-dnp-tiktok-video-loading"
                >
                  <div className="w-3 h-3 rounded-full border-2 border-emerald-400 border-t-transparent animate-spin" />
                  Composing voiceover + rendering slides + ffmpeg mux… please hold.
                </div>
              )}

              {videoState.status === "ready" && videoState.url && (
                <div className="mt-3 flex flex-col md:flex-row gap-3" data-testid="admin-dnp-tiktok-video-ready">
                  <video
                    src={`${API_BASE}${videoState.url}`}
                    controls
                    playsInline
                    data-testid="admin-dnp-tiktok-video-player"
                    className="w-40 sm:w-48 rounded-md bg-black border border-white/10"
                    style={{ aspectRatio: "9/16" }}
                  />
                  <div className="flex-1 flex flex-col gap-2 text-[13px] text-white/70">
                    <div className="text-white font-bold">
                      Ready to post to TikTok
                    </div>
                    <div className="text-[12px] text-white/50">
                      Duration: {videoState.duration ? videoState.duration.toFixed(1) : "?"}s ·
                      Voice: <b className="text-white">{videoVoice}</b> ·
                      {videoState.cached ? " Loaded from cache" : " Freshly rendered"}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-1">
                      <a
                        href={`${API_BASE}${videoState.url}`}
                        download
                        data-testid="admin-dnp-tiktok-video-download"
                        className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-bold bg-emerald-500 hover:bg-emerald-400 text-black transition-colors"
                      >
                        <Download size={12} weight="bold" /> Download MP4
                      </a>
                      <a
                        href="https://www.tiktok.com/upload"
                        target="_blank"
                        rel="noreferrer"
                        data-testid="admin-dnp-tiktok-video-upload-link"
                        className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-bold bg-white/10 hover:bg-white/20 text-white border border-white/10 transition-colors"
                      >
                        Upload to TikTok (@freightpilotai) →
                      </a>
                      <button
                        type="button"
                        onClick={() => generateVideo(true)}
                        data-testid="admin-dnp-tiktok-video-regenerate"
                        className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-bold bg-white/10 hover:bg-white/20 text-white border border-white/10 transition-colors"
                      >
                        <ArrowClockwise size={12} weight="bold" /> Regenerate
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {videoState.status === "error" && (
                <div className="mt-3 text-[12px] text-red-300 font-mono" data-testid="admin-dnp-tiktok-video-error">
                  Generation failed — see toast above for details.
                </div>
              )}
            </div>

            {/* Hook */}
            <div className="rounded-xl border-2 border-[#FF5A1F]/40 bg-[#FF5A1F]/[0.06] p-4" data-testid="admin-dnp-tiktok-hook">
              <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-1">Opening hook</div>
              <div className="text-lg font-black text-white leading-tight">{data.script.hook}</div>
            </div>

            {/* Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {(data.script.cards || []).map((c, i) => (
                <div key={i} className="rounded-xl border border-white/10 bg-white/[0.03] p-3.5" data-testid={`admin-dnp-tiktok-card-${i}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">Card {i + 1}</div>
                    <div className="text-[10px] text-white/40 font-mono">{c.seconds}s</div>
                  </div>
                  <div className="text-white text-sm font-bold leading-snug mb-2" data-testid={`admin-dnp-tiktok-card-text-${i}`}>
                    "{c.on_screen_text}"
                  </div>
                  <div className="text-[12px] text-white/60 leading-relaxed">
                    <span className="text-[9px] uppercase tracking-widest text-white/40 font-bold block mb-1">Spoken</span>
                    {c.spoken}
                  </div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/[0.06] p-4" data-testid="admin-dnp-tiktok-cta">
              <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold mb-1">Closing CTA</div>
              <div className="text-white text-sm font-semibold leading-snug">{data.script.cta}</div>
            </div>

            {/* Hashtags */}
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">Hashtags</div>
                <button onClick={() => copy((data.script.hashtags || []).join(" "), "Hashtags")}
                  data-testid="admin-dnp-tiktok-copy-hashtags"
                  className="text-[11px] text-[#FF5A1F] hover:text-[#FF7038] font-bold flex items-center gap-1">
                  <Copy size={11} weight="bold" /> Copy
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="admin-dnp-tiktok-hashtags">
                {(data.script.hashtags || []).map((h, i) => (
                  <span key={i} className="text-[12px] font-mono text-white/80 bg-white/[0.06] rounded px-2 py-1 border border-white/10">
                    {h}
                  </span>
                ))}
              </div>
            </div>

            {/* B-roll */}
            {(data.script.b_roll_tips || []).length > 0 && (
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold mb-2">B-roll tips</div>
                <ul className="space-y-1.5 text-[12px] text-white/70 leading-relaxed">
                  {data.script.b_roll_tips.map((tip, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-[#FF5A1F] shrink-0">■</span>
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
      {teleprompterOn && data?.script && (
        <TeleprompterOverlay
          data={data}
          onClose={() => setTeleprompterOn(false)}
        />
      )}
    </div>
  );
}


/* ─────────────────────  TELEPROMPTER OVERLAY  ─────────────────────
   Fullscreen scrolling teleprompter for filming. Purpose: founder points
   the phone camera at the laptop screen, hits Play, reads the script as
   it scrolls at a steady pace. Zero second screen needed.

   Controls:
     • Space / Play button — play/pause
     • ↑ / ↓ — nudge speed
     • R — restart from top
     • Esc — exit overlay (back to the script modal)
     • Speed slider (30-140 px/sec, default 55 for ~15sec run)
     • Font size slider (32-88 px, default 56)
     • Mirror toggle — flips the text horizontally for prompter rigs

   The script body is composed the same way `copyAll()` builds the
   clipboard text so what you rehearse matches what you post. */

const TELEPROMPTER_DEFAULT_SPEED = 55;
const TELEPROMPTER_DEFAULT_FONT = 56;
const TELEPROMPTER_RECORD_SECONDS = 15;   // TikTok short target length

function _composeTeleprompterText(data) {
  if (!data?.script) return "";
  const s = data.script;
  const lines = [];
  lines.push("HOOK");
  lines.push(s.hook || "");
  lines.push("");
  (s.cards || []).forEach((c, i) => {
    lines.push(`CARD ${i + 1} · ${c.seconds}s`);
    if (c.on_screen_text) lines.push(`On-screen: "${c.on_screen_text}"`);
    if (c.spoken) lines.push(c.spoken);
    lines.push("");
  });
  lines.push("CTA");
  lines.push(s.cta || "");
  return lines.join("\n");
}

function CountdownBadge({ secondsRemaining, isCut, playing }) {
  const secs = Math.ceil(secondsRemaining);
  const total = 15;
  const isLastThree = secs <= 3 && !isCut && playing;

  // At CUT — a big pulsing red banner that overtakes the countdown.
  if (isCut) {
    return (
      <div
        aria-live="assertive"
        data-testid="admin-dnp-teleprompter-timer-cut"
        className="pointer-events-none fixed top-20 left-1/2 -translate-x-1/2 z-[70] flex items-center gap-3 rounded-full bg-red-600 text-white px-5 py-2.5 text-2xl font-black tracking-widest shadow-2xl animate-pulse"
      >
        ● CUT
      </div>
    );
  }

  // Circular ring showing time-left as a shrinking arc; digits in the
  // middle. Grey when not playing, orange while filming, red pulse in
  // the last 3 seconds.
  const pctLeft = Math.max(0, Math.min(1, secondsRemaining / total));
  const circumference = 2 * Math.PI * 26; // r=26
  const dashOffset = circumference * (1 - pctLeft);
  const ringColor = isLastThree ? "#dc2626" : (playing ? "#FF5A1F" : "#3f3f46");

  return (
    <div
      aria-live="polite"
      data-testid="admin-dnp-teleprompter-timer"
      className={`pointer-events-none fixed top-20 left-1/2 -translate-x-1/2 z-[70] flex items-center gap-2 rounded-full bg-black/70 backdrop-blur-md border border-white/10 px-2.5 py-1.5 shadow-2xl transition-transform ${isLastThree ? "animate-pulse scale-110" : ""}`}
    >
      <svg width="52" height="52" viewBox="0 0 60 60" className="shrink-0">
        <circle cx="30" cy="30" r="26" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="4" />
        <circle
          cx="30" cy="30" r="26"
          fill="none"
          stroke={ringColor}
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          transform="rotate(-90 30 30)"
          style={{ transition: "stroke-dashoffset 0.15s linear, stroke 0.2s" }}
        />
        <text
          x="30" y="35"
          textAnchor="middle"
          fill={isLastThree ? "#fca5a5" : "#ffffff"}
          fontFamily="monospace"
          fontSize="18"
          fontWeight="900"
        >
          {secs}
        </text>
      </svg>
      <div className="pr-2">
        <div className={`text-[10px] uppercase tracking-widest font-bold ${isLastThree ? "text-red-300" : "text-white/60"}`}>
          {playing ? (isLastThree ? "Land the CTA" : "Recording") : "Ready"}
        </div>
        <div className="text-[11px] text-white/50 font-mono">
          {playing ? `${secs}s left of ${total}s` : `${total}s TikTok cap`}
        </div>
      </div>
    </div>
  );
}


function TeleprompterOverlay({ data, onClose }) {
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(TELEPROMPTER_DEFAULT_SPEED);       // px per second
  const [fontSize, setFontSize] = useState(TELEPROMPTER_DEFAULT_FONT);  // px
  const [mirrored, setMirrored] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const scrollRef = useRef(null);
  const rafRef = useRef(null);
  const lastTsRef = useRef(0);
  const timerStartRef = useRef(0);

  const scriptText = _composeTeleprompterText(data);
  const secondsRemaining = Math.max(0, TELEPROMPTER_RECORD_SECONDS - elapsedMs / 1000);
  const isCut = elapsedMs / 1000 >= TELEPROMPTER_RECORD_SECONDS;

  const step = useCallback((ts) => {
    if (!scrollRef.current) return;
    if (!lastTsRef.current) lastTsRef.current = ts;
    const dt = (ts - lastTsRef.current) / 1000;
    lastTsRef.current = ts;
    scrollRef.current.scrollTop += speed * dt;
    // Advance the record timer at the same wall-clock rate as scroll so the
    // timer stays in sync even if speed is tweaked mid-take.
    setElapsedMs((prev) => prev + (dt * 1000));
    // Stop when we hit the bottom
    const el = scrollRef.current;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 4) {
      setPlaying(false);
      return;
    }
    rafRef.current = requestAnimationFrame(step);
  }, [speed]);

  useEffect(() => {
    if (playing) {
      lastTsRef.current = 0;
      // Kick the timer clock only on the very first Play after a Restart.
      if (elapsedMs === 0) timerStartRef.current = performance.now();
      rafRef.current = requestAnimationFrame(step);
    }
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, step]);

  const restart = () => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    setElapsedMs(0);
    setPlaying(true);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { onClose(); return; }
      if (e.code === "Space") { e.preventDefault(); setPlaying((p) => !p); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setSpeed((s) => Math.min(140, s + 5)); return; }
      if (e.key === "ArrowDown") { e.preventDefault(); setSpeed((s) => Math.max(30, s - 5)); return; }
      if (e.key === "r" || e.key === "R") { restart(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] bg-black flex flex-col"
      data-testid="admin-dnp-tiktok-teleprompter"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Floating countdown timer — sits above the reading line so the founder's
          eye catches it without looking down. Red pulse at <=3s. "CUT" replaces
          the digits at 0. */}
      <CountdownBadge
        secondsRemaining={secondsRemaining}
        isCut={isCut}
        playing={playing}
      />

      {/* Header controls */}
      <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-6 py-3 border-b border-white/10 bg-black/60 backdrop-blur-md">
        <div className="flex items-center gap-2 mr-auto min-w-0">
          <div className="w-8 h-8 rounded bg-[#FF5A1F]/20 text-[#FF5A1F] flex items-center justify-center shrink-0">
            <Monitor size={16} weight="bold" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-bold">Teleprompter</div>
            <div className="text-sm font-bold text-white truncate">{data.broker || "TikTok script"}</div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setPlaying((p) => !p)}
          data-testid="admin-dnp-teleprompter-play"
          className={`inline-flex items-center gap-1.5 rounded-md px-3.5 py-2 text-xs font-bold transition-colors ${playing ? "bg-white/20 hover:bg-white/25 text-white" : "bg-[#FF5A1F] hover:bg-[#FF7038] text-black"}`}
        >
          {playing ? (<><Pause size={12} weight="bold" /> Pause</>) : (<><Play size={12} weight="bold" /> Play</>)}
        </button>
        <button
          type="button"
          onClick={restart}
          data-testid="admin-dnp-teleprompter-restart"
          className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-bold bg-white/10 hover:bg-white/20 text-white border border-white/10 transition-colors"
        >
          <ArrowClockwise size={12} weight="bold" /> Restart
        </button>

        <div className="hidden md:flex items-center gap-2 text-[11px] text-white/60">
          <span className="font-mono">Speed</span>
          <input
            type="range"
            min="30" max="140" step="5"
            value={speed}
            onChange={(e) => setSpeed(Number(e.target.value))}
            data-testid="admin-dnp-teleprompter-speed"
            className="w-24 accent-[#FF5A1F]"
          />
          <span className="font-mono w-9 tabular-nums">{speed}</span>
        </div>
        <div className="hidden md:flex items-center gap-2 text-[11px] text-white/60">
          <span className="font-mono">Size</span>
          <input
            type="range"
            min="32" max="88" step="2"
            value={fontSize}
            onChange={(e) => setFontSize(Number(e.target.value))}
            data-testid="admin-dnp-teleprompter-fontsize"
            className="w-24 accent-[#FF5A1F]"
          />
          <span className="font-mono w-9 tabular-nums">{fontSize}</span>
        </div>
        <button
          type="button"
          onClick={() => setMirrored((m) => !m)}
          data-testid="admin-dnp-teleprompter-mirror"
          className={`hidden sm:inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-bold border transition-colors ${mirrored ? "bg-[#FF5A1F]/20 text-[#FF5A1F] border-[#FF5A1F]/40" : "bg-white/10 hover:bg-white/20 text-white border-white/10"}`}
        >
          Mirror
        </button>
        <button
          type="button"
          onClick={onClose}
          data-testid="admin-dnp-teleprompter-close"
          className="w-9 h-9 rounded-lg hover:bg-white/10 text-white/60 flex items-center justify-center transition-colors shrink-0"
        >
          <XIcon size={18} weight="bold" />
        </button>
      </div>

      {/* Scrolling body */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto"
        style={{
          transform: mirrored ? "scaleX(-1)" : "none",
          scrollbarWidth: "thin",
        }}
      >
        {/* Reading-line indicator */}
        <div
          aria-hidden
          className="pointer-events-none sticky top-1/3 z-10 mx-auto h-[2px] w-24 rounded-full bg-[#FF5A1F]/60 shadow-[0_0_16px_rgba(255,90,31,0.4)]"
        />
        {/* Top spacer so text starts below the reading line */}
        <div style={{ height: "40vh" }} />
        <div
          data-testid="admin-dnp-teleprompter-text"
          className="mx-auto max-w-4xl px-6 sm:px-10 pb-[60vh] font-bold text-white leading-tight tracking-tight"
          style={{
            fontSize: `${fontSize}px`,
            whiteSpace: "pre-wrap",
          }}
        >
          {scriptText}
        </div>
      </div>

      {/* Bottom hint */}
      <div className="px-4 py-2 border-t border-white/10 text-center text-[10px] uppercase tracking-widest text-white/40 font-bold bg-black/60">
        Space play/pause · ↑↓ speed · R restart · Esc exit
      </div>
    </div>
  );
}




/* ─────────────────── 1-Click AI Video Generator ───────────────────
 *
 * Promo-only. Renders positive, signup-driving FreightPilotAI videos —
 * zero scam angles, zero fear-based hooks. Picks a topic from
 * `promo_script.PROMO_TOPICS` (reclaim_time, founder_story, etc.),
 * writes the script with Claude, voices it with OpenAI TTS, renders
 * the animated MP4 at the chosen duration + voice.
 *
 * Two flows:
 *   1. Single: pick topic → render one video (~30-90s)
 *   2. Batch:  render all 7 topics → ZIP (~2-4 min)
 *
 * State machine (single):
 *   idle  → user hasn't clicked yet
 *   picking → generating (one POST call)
 *   ready → mp4_url returned, show preview + download
 *   error → toast + retry
 *
 * State machine (batch):
 *   idle  → hasn't clicked "Generate All 7"
 *   generating → POST in flight, spinner
 *   ready → zip_url + per-topic result grid
 *   error → toast + retry
 * */
/* Shared busy indicator with elapsed-time counter — pops after 15s of
 * waiting so long renders (90s/3-min/batch) don't feel frozen. Reused
 * by the single, batch, and A/B pair loading states. Progressive hint
 * text after 60s / 180s / 360s reassures the user that longer renders
 * are normal and no refresh is needed. */
function BusyIndicator({ label, testId, accent = "emerald" }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const t0 = Date.now();
    const id = setInterval(() => setElapsed(Math.round((Date.now() - t0) / 1000)), 1000);
    return () => clearInterval(id);
  }, []);
  const spinnerClass =
    accent === "amber"
      ? "border-amber-400 border-t-transparent"
      : "border-emerald-400 border-t-transparent";
  // Progressive hint — reassures the user during long production renders
  const hint =
    elapsed > 360 ? "Almost done — 90s+ videos on production can take up to 10 min. Don't refresh."
    : elapsed > 180 ? "Long-form video is rendering — hold tight, no need to refresh."
    : elapsed > 60 ? "Still rendering (normal for 60s+ videos on production)…"
    : null;
  return (
    <div className="mt-3 flex flex-col gap-1" data-testid={testId}>
      <div className="text-[12px] text-white/70 flex items-center gap-2">
        <div className={`w-3 h-3 rounded-full border-2 animate-spin ${spinnerClass}`} />
        <span>{label}</span>
        {elapsed > 3 && (
          <span
            className="text-[11px] font-mono text-white/50 ml-1"
            data-testid={`${testId}-elapsed`}
          >
            · {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, "0")} elapsed
          </span>
        )}
      </div>
      {hint && (
        <div
          className="text-[11px] text-amber-300/80 ml-5 italic"
          data-testid={`${testId}-hint`}
        >
          {hint}
        </div>
      )}
    </div>
  );
}


/* ─────────── Reel Library — preview grid + one-tap Regenerate All ─────────── */
function ChampionCameosCard() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const r = await client.get("/admin/champion-cameos");
      setRows(r.data?.cameos || []);
    } catch { /* card hides */ }
  };
  useEffect(() => { load(); }, []);

  const togglePublish = async (r) => {
    setBusy(r.cache_key);
    try {
      await client.post(`/admin/champion-cameos/${r.cache_key}/publish`, { published: !r.published });
      toast.success(!r.published ? "Live on /watch" : "Removed from /watch");
      await load();
    } catch { toast.error("Could not update publish state"); }
    finally { setBusy(null); }
  };

  if (!rows.length) return null;
  return (
    <div className="mb-6 rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="champion-cameos-card">
      <div className="flex items-center gap-2 mb-3">
        <span className="overline">CHAMPION SHOUT-OUTS · CUSTOMER-RECORDED REELS</span>
        <span className="ml-auto text-[10px] font-mono text-neutral-400">{rows.length} recorded</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        {rows.map((r) => (
          <div key={r.cache_key} data-testid={`champion-cameo-${r.cache_key}`}
               className={`rounded-lg overflow-hidden border ${r.published ? "border-emerald-400/50" : "border-white/10"} bg-black`}>
            <video src={r.cloudinary_cdn_url} controls playsInline preload="metadata"
                   className="w-full aspect-[9/16] object-cover" />
            <div className="p-2">
              <div className="text-[11px] font-bold text-white truncate" title={r.champion_company}>
                {r.champion_company || r.topic_label}
              </div>
              <div className="text-[9px] font-mono text-neutral-500">{r.requested_duration_sec}s</div>
              <button type="button" onClick={() => togglePublish(r)} disabled={busy === r.cache_key}
                      data-testid={`champion-cameo-publish-${r.cache_key}`}
                      className={`mt-1.5 w-full rounded-md px-2 py-1 text-[10px] font-bold transition-colors ${
                        r.published
                          ? "bg-white/10 hover:bg-white/20 text-white"
                          : "bg-emerald-500 hover:bg-emerald-400 text-black"
                      }`}>
                {busy === r.cache_key ? "…" : r.published ? "Unpublish" : "Publish to /watch"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReelLibraryCard() {
  const [reels, setReels] = useState([]);
  const [ttRank, setTtRank] = useState(false);
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [zipping, setZipping] = useState(false);

  const downloadAll = async () => {
    setZipping(true);
    try {
      const r = await client.get("/admin/dnp/tiktok-video/download-all", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "freightpilot_reels.zip";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Zip downloaded — batch-post away");
    } catch { toast.error("Zip failed — try again"); }
    finally { setZipping(false); }
  };

  const load = async () => {
    try {
      const r = await client.get("/admin/dnp/tiktok-video/library");
      setReels(r.data?.reels || []);
    } catch { /* grid stays empty */ }
  };
  const editTikTokViews = async (r) => {
    const raw = window.prompt(`TikTok views for "${r.label}" (${r.duration_sec}s)? Check the TikTok app and enter the number.`, String(r.tiktok_views || 0));
    if (raw == null) return;
    const v = parseInt(String(raw).replace(/[^0-9]/g, ""), 10);
    if (isNaN(v)) { toast.error("Enter a number"); return; }
    try {
      await client.post("/admin/dnp/tiktok-video/library/tiktok-views", { topic: r.topic, duration_sec: r.duration_sec, views: v });
      toast.success("TikTok views saved");
      load();
    } catch { toast.error("Couldn't save views"); }
  };
  const postTopReel = () => {
    const ranked = [...reels].sort((a, b) =>
      (b.tiktok_views || 0) - (a.tiktok_views || 0) || (b.views || 0) - (a.views || 0));
    const top = ranked[0];
    if (!top) return;
    const utm = `?utm_source=tiktok&utm_medium=organic&utm_campaign=top_reel&utm_content=${top.topic}-${top.duration_sec}s`;
    navigator.clipboard.writeText(promoCaption(top.label, utm));
    if (top.video_url) window.open(top.video_url, "_blank");
    client.post("/admin/dnp/post-log").catch(() => {});
    toast.success(`Copied caption + link for "${top.label}" — go post it 📣`);
  };
  const loadJob = async () => {
    try {
      const r = await client.get("/admin/dnp/tiktok-video/regenerate-all/status");
      setJob(r.data?.job || null);
      return r.data?.job;
    } catch { return null; }
  };

  useEffect(() => { load(); loadJob(); }, []);

  useEffect(() => {
    if (job?.status !== "running") return;
    const id = setInterval(async () => {
      const j = await loadJob();
      if (j?.status !== "running") { clearInterval(id); load(); }
    }, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.status]);

  const startRegen = async () => {
    if (!window.confirm("Rebuild EVERY reel with the newest look? Takes ~30-45 min and runs in the background — you can leave this page.")) return;
    setStarting(true);
    try {
      const r = await client.post("/admin/dnp/tiktok-video/regenerate-all");
      if (r.data?.status === "already_running") toast.info("A rebuild is already running");
      else toast.success("Rebuilding all reels in the background — check back in ~30 min");
      await loadJob();
    } catch { toast.error("Could not start the rebuild"); }
    finally { setStarting(false); }
  };

  const running = job?.status === "running";
  const fresh = (iso) => {
    if (!iso) return "";
    const h = (Date.now() - new Date(iso).getTime()) / 36e5;
    return h < 24 ? "fresh today" : h < 168 ? `${Math.round(h / 24)}d ago` : `${Math.round(h / 168)}w ago`;
  };

  return (
    <div className="mb-6 rounded-2xl border border-white/10 bg-black/40 p-5" data-testid="reel-library-card">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-sky-300 font-bold mb-1">Reel Library</div>
          <div className="text-lg font-black text-white leading-tight" data-testid="reel-library-title">
            Every reel at a glance {reels.length > 0 && <span className="text-white/40 font-mono text-sm">· {reels.length}</span>}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-3">
          {running && (
            <span className="text-[11px] font-mono text-amber-300" data-testid="regen-all-progress">
              Rebuilding {job.done}/{job.total || "…"}{job.current ? ` — ${job.current}` : ""}
            </span>
          )}
          <button
            type="button"
            onClick={postTopReel}
            disabled={reels.length === 0}
            data-testid="post-top-reel-btn"
            className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-bold border border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/10 transition-colors disabled:opacity-50"
            title="Copies the caption + UTM link for your top-ranked reel and opens the video"
          >
            📣 Post top reel
          </button>
          <button
            type="button"
            onClick={() => setTtRank((v) => !v)}
            disabled={reels.length === 0}
            data-testid="tt-rank-toggle"
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-bold border transition-colors disabled:opacity-50 ${
              ttRank
                ? "bg-sky-500 border-sky-500 text-black"
                : "border-white/15 text-white hover:bg-white/10"
            }`}
            title="Rank reels by the TikTok view counts you've entered"
          >
            🏆 {ttRank ? "Ranked by TikTok views" : "Rank by TikTok views"}
          </button>
          <button
            type="button"
            onClick={downloadAll}
            disabled={zipping || reels.length === 0}
            data-testid="download-all-btn"
            className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-bold border border-white/15 text-white hover:bg-white/10 transition-colors disabled:opacity-50"
          >
            <Download size={14} weight="bold" />
            {zipping ? "Zipping…" : "Download all (.zip)"}
          </button>
          <button
            type="button"
            onClick={startRegen}
            disabled={starting || running}
            data-testid="regen-all-btn"
            className="inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-bold bg-sky-500 hover:bg-sky-400 text-black transition-colors disabled:opacity-50"
          >
            <ArrowClockwise size={14} weight="bold" />
            {running ? "Rebuilding…" : "Regenerate all with newest look"}
          </button>
        </div>
      </div>
      {reels.length === 0 ? (
        <div className="text-[12px] text-white/40" data-testid="reel-library-empty">
          No reels rendered yet — generate one above and it shows up here.
        </div>
      ) : (() => {
        const maxViews = Math.max(...reels.map((r) => r.views || 0));
        const champKey = maxViews > 0
          ? reels.find((r) => (r.views || 0) === maxViews) : null;
        const maxTT = Math.max(...reels.map((r) => r.tiktok_views || 0));
        const ttChamp = maxTT > 0
          ? reels.find((r) => (r.tiktok_views || 0) === maxTT) : null;
        const shown = ttRank
          ? [...reels].sort((a, b) => (b.tiktok_views || 0) - (a.tiktok_views || 0))
          : reels;
        return (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3" data-testid="reel-library-grid">
          {shown.map((r, idx) => {
            const isChamp = champKey && r.topic === champKey.topic && r.duration_sec === champKey.duration_sec;
            return (
            <button
              key={`${r.topic}-${r.duration_sec}`}
              type="button"
              onClick={() => r.video_url && window.open(r.video_url, "_blank")}
              data-testid={`reel-thumb-${r.topic}-${r.duration_sec}`}
              className={`group text-left rounded-lg overflow-hidden border bg-black/60 transition-colors ${
                isChamp ? "border-yellow-400/70" : "border-white/10 hover:border-sky-400/60"
              }`}
              title={`${r.label} · ${r.duration_sec}s — click to watch`}
            >
              <div className="relative aspect-[9/16] bg-neutral-900">
                {r.thumb_url ? (
                  <img src={r.thumb_url} alt={r.label} loading="lazy"
                       className="w-full h-full object-cover group-hover:opacity-90 transition-opacity" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/20">
                    <FilmSlate size={28} weight="duotone" />
                  </div>
                )}
                <span className="absolute top-1 right-1 rounded bg-black/80 px-1.5 py-0.5 text-[9px] font-mono text-white">
                  {r.duration_sec >= 180 ? "3min" : `${r.duration_sec}s`}
                </span>
                {ttRank && (r.tiktok_views || 0) > 0 && (
                  <span
                    className={`absolute top-1 left-1 rounded px-1.5 py-0.5 text-[10px] font-black ${
                      idx === 0 ? "bg-yellow-400 text-black" : idx === 1 ? "bg-neutral-300 text-black" : idx === 2 ? "bg-amber-600 text-black" : "bg-black/80 text-white"
                    }`}
                    data-testid={`reel-tt-rank-${r.topic}-${r.duration_sec}`}
                  >
                    #{idx + 1}
                  </span>
                )}
                {isChamp && (
                  <span className="absolute top-1 left-1 rounded bg-yellow-400 px-1.5 py-0.5 text-[9px] font-black text-black"
                        data-testid={`reel-champ-${r.topic}-${r.duration_sec}`}>
                    👑 MOST WATCHED
                  </span>
                )}
                {ttChamp && r.topic === ttChamp.topic && r.duration_sec === ttChamp.duration_sec && (
                  <span className="absolute bottom-1 left-1 rounded bg-sky-400 px-1.5 py-0.5 text-[9px] font-black text-black"
                        data-testid={`reel-tt-champ-${r.topic}-${r.duration_sec}`}>
                    🔥 TOP ON TIKTOK
                  </span>
                )}
              </div>
              <div className="p-1.5">
                <div className="text-[10px] font-semibold text-white leading-tight truncate">{r.label}</div>
                <div className="text-[9px] font-mono flex items-center gap-1.5 flex-wrap">
                  <span className="text-white/40">
                    {fresh(r.generated_at)}{r.views > 0 ? ` · ${r.views} view${r.views === 1 ? "" : "s"}` : ""}
                  </span>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); editTikTokViews(r); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); editTikTokViews(r); } }}
                    data-testid={`reel-tt-views-${r.topic}-${r.duration_sec}`}
                    className="text-sky-300/80 hover:text-sky-200 underline decoration-dotted cursor-pointer"
                    title="Click to enter this reel's TikTok view count"
                  >
                    {r.tiktok_views > 0
                      ? `TT ${r.tiktok_views >= 1000 ? `${(r.tiktok_views / 1000).toFixed(1)}k` : r.tiktok_views}`
                      : "+TT"}
                  </span>
                  {r.tiktok_views_delta != null && r.tiktok_views_delta !== 0 && (
                    <span
                      data-testid={`reel-tt-trend-${r.topic}-${r.duration_sec}`}
                      className={r.tiktok_views_delta > 0 ? "text-emerald-400" : "text-red-400"}
                      title="Change since your last TikTok views entry"
                    >
                      {r.tiktok_views_delta > 0 ? "▲" : "▼"}
                      {Math.abs(r.tiktok_views_delta) >= 1000
                        ? `${(Math.abs(r.tiktok_views_delta) / 1000).toFixed(1)}k`
                        : Math.abs(r.tiktok_views_delta)}
                    </span>
                  )}
                </div>
              </div>
            </button>
            );
          })}
        </div>
        );
      })()}
    </div>
  );
}


function OneClickTikTokCard() {
  const [promoTopics, setPromoTopics] = useState([]);
  const [topic, setTopic] = useState("reclaim_time");
  const [duration, setDuration] = useState(15);
  const [voice, setVoice] = useState("onyx");
  const [state, setState] = useState({ status: "idle" });
  const [batchState, setBatchState] = useState({ status: "idle" });
  const [abState, setAbState] = useState({ status: "idle" });
  const [abMode, setAbMode] = useState("length");
  const [abTopic, setAbTopic] = useState("founder_story");
  const [captionDefault, setCaptionDefault] = useState(null);

  useEffect(() => {
    // Load promo topic catalog once for the dropdown
    (async () => {
      try {
        const r = await client.get("/admin/dnp/tiktok-video/promo-topics");
        setPromoTopics(r.data?.topics || []);
        if (r.data?.default) setTopic(r.data.default);
      } catch {
        setPromoTopics([]);
      }
    })();
    client.get("/admin/dnp/caption-style-default")
      .then((r) => setCaptionDefault(r.data?.style || "classic"))
      .catch(() => {});
  }, []);

  // Poll a background render job every 3s until status=done|error.
  // Times out after `timeoutMs` — throws with the last-known status.
  // Never lets Cloudflare's 60s edge timeout kill a long render.
  const pollJob = async (jobId, timeoutMs = 300_000) => {
    const start = Date.now();
    let attempts = 0;
    while (Date.now() - start < timeoutMs) {
      attempts += 1;
      // First poll is fast (1s) so the UI feels snappy; subsequent
      // polls throttle to 3s to avoid hammering the backend.
      await new Promise((r) => setTimeout(r, attempts === 1 ? 1000 : 3000));
      let doc;
      try {
        const r = await client.get(`/admin/dnp/tiktok-video/job/${jobId}`, { timeout: 10_000 });
        doc = r.data;
      } catch (e) {
        // Transient network blip — keep polling instead of failing
        continue;
      }
      if (doc?.status === "done") return doc.result;
      if (doc?.status === "error") {
        throw new Error(doc.error || "Render failed");
      }
    }
    throw new Error(`Render timed out after ${Math.round(timeoutMs / 1000)}s`);
  };

  const run = async (force = false) => {
    setState({ status: "picking" });
    try {
      // Async kick — returns instantly with job_id. Then poll every 3s
      // until done. Kills the Cloudflare 60s edge timeout completely.
      const kickUrl = `/admin/dnp/tiktok-video/promo/async?topic=${encodeURIComponent(topic)}&voice=${encodeURIComponent(voice)}&duration_sec=${duration}${force ? "&force=true" : ""}`;
      const kick = await client.post(kickUrl, null, { timeout: 15_000 });
      const jobId = kick.data?.job_id;
      if (!jobId) throw new Error("No job_id returned");
      const res = await pollJob(jobId, 600_000); // 10 min — production ffmpeg can be slow under load
      setState({
        status: "ready",
        url: res.mp4_url,
        cdnUrl: res.cdn_url,
        srtUrl: res.srt_url,
        duration: res.duration_sec,
        topicLabel: res.topic_label || null,
        cached: res.cached,
      });
      const label = res.topic_label || "promo video";
      toast.success(
        res.cached ? `Loaded cached: ${label}` : `Generated: ${label}`,
      );
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Video generation failed";
      setState({ status: "error", error: String(msg) });
      toast.error(String(msg));
    }
  };

  const runBatch = async (force = false) => {
    setBatchState({ status: "generating" });
    try {
      const kickUrl = `/admin/dnp/tiktok-video/promo-batch/async?voice=${encodeURIComponent(voice)}&duration_sec=${duration}${force ? "&force=true" : ""}`;
      const kick = await client.post(kickUrl, null, { timeout: 15_000 });
      const jobId = kick.data?.job_id;
      if (!jobId) throw new Error("No job_id returned");
      const res = await pollJob(jobId, 1_800_000); // 30 min max — 7 × 3-min renders can hit that
      setBatchState({
        status: "ready",
        zipUrl: res.zip_url,
        results: res.results || [],
        successCount: res.success_count || 0,
        totalCount: res.total_count || 0,
      });
      toast.success(`Batch ready — ${res.success_count}/${res.total_count} promo videos`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Batch failed";
      setBatchState({ status: "error", error: String(msg) });
      toast.error(String(msg));
    }
  };

  const runAb = async (force = false) => {
    // A/B pair — renders 30s (punchy) + 90s (long-form) of the SAME topic
    // in parallel so the founder can split-test which converts better.
    setAbState({ status: "generating" });
    try {
      const kickUrl = abMode === "captions"
        ? `/admin/dnp/tiktok-video/promo-caption-ab/async?topic=${encodeURIComponent(abTopic)}&voice=${encodeURIComponent(voice)}&duration_sec=30${force ? "&force=true" : ""}`
        : `/admin/dnp/tiktok-video/promo-ab/async?topic=${encodeURIComponent(abTopic)}&voice=${encodeURIComponent(voice)}&short_sec=30&long_sec=90${force ? "&force=true" : ""}`;
      const kick = await client.post(kickUrl, null, { timeout: 15_000 });
      const jobId = kick.data?.job_id;
      if (!jobId) throw new Error("No job_id returned");
      const res = await pollJob(jobId, 600_000); // 10 min max
      setAbState({
        status: "ready",
        mode: abMode,
        topicLabel: res.topic_label,
        a: res.a,
        b: res.b,
        utmHints: res.utm_hints,
      });
      toast.success(`A/B pair ready — ${res.topic_label}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "A/B pair failed";
      setAbState({ status: "error", error: String(msg) });
      toast.error(String(msg));
    }
  };

  const busy = state.status === "picking";
  const batchBusy = batchState.status === "generating";
  const abBusy = abState.status === "generating";
  const selectedTopic = promoTopics.find((t) => t.key === topic);

  return (
    <div
      className="mb-6 rounded-2xl border-2 border-emerald-500/40 bg-gradient-to-br from-emerald-500/[0.08] via-black/40 to-black/60 p-5"
      data-testid="admin-dnp-one-click-card"
    >
      <div className="flex flex-col md:flex-row md:items-start gap-4">
        <div className="w-12 h-12 shrink-0 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 flex items-center justify-center">
          <FilmSlate size={22} weight="duotone" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold mb-1">
            Promo Video Generator
          </div>
          <div className="text-lg font-black text-white leading-tight" data-testid="admin-dnp-one-click-title">
            Generate today's TikTok
          </div>
          <div className="text-[12px] text-white/60 mt-1 leading-snug max-w-lg">
            Positive, signup-driving promo content — no scam angles, no fear hooks. Animated gradients, Ken-Burns zooms, punchy typography.
            Ready to upload to <span className="text-emerald-300 font-mono">@freightpilotai</span>.
          </div>

          {/* Promo topic picker */}
          {promoTopics.length > 0 && (
            <div className="mt-4 flex flex-col gap-1">
              <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold">
                Promo topic
              </label>
              <select
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                disabled={busy}
                data-testid="admin-dnp-one-click-topic"
                className="text-sm bg-black/50 border border-white/10 rounded px-2 py-1.5 text-white font-mono max-w-md"
              >
                {promoTopics.map((t) => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
              {selectedTopic && (
                <div className="text-[11px] text-white/50 mt-1 max-w-md" data-testid="admin-dnp-one-click-topic-pitch">
                  {selectedTopic.pitch}
                </div>
              )}
            </div>
          )}

          {/* Controls row */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <div
              className="inline-flex rounded-md bg-black/50 border border-white/10 p-0.5 flex-wrap"
              data-testid="admin-dnp-one-click-duration"
              role="radiogroup"
              aria-label="Video duration"
            >
              {[15, 30, 60, 90, 180].map((d) => (
                <button
                  key={d}
                  type="button"
                  role="radio"
                  aria-checked={duration === d}
                  onClick={() => setDuration(d)}
                  disabled={busy}
                  data-testid={`admin-dnp-one-click-duration-${d}`}
                  className={`px-3 py-1.5 text-[11px] font-bold rounded transition-colors font-mono disabled:opacity-50 ${
                    duration === d
                      ? "bg-emerald-500 text-black"
                      : "text-white/70 hover:text-white"
                  }`}
                >
                  {d === 180 ? "3min" : `${d}s`}
                </button>
              ))}
            </div>

            <select
              value={voice}
              onChange={(e) => setVoice(e.target.value)}
              disabled={busy}
              data-testid="admin-dnp-one-click-voice"
              className="text-xs bg-black/50 border border-white/10 rounded px-2 py-1.5 text-white font-mono"
            >
              <option value="onyx">onyx · deep + trucker</option>
              <option value="echo">echo · smooth</option>
              <option value="nova">nova · energetic</option>
              <option value="ash">ash · articulate</option>
              <option value="sage">sage · wise</option>
            </select>

            <button
              type="button"
              onClick={() => run(false)}
              disabled={busy}
              data-testid="admin-dnp-one-click-generate"
              className="inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-bold bg-emerald-500 hover:bg-emerald-400 text-black transition-colors disabled:opacity-50"
            >
              <FilmSlate size={14} weight="bold" />
              {busy
                ? "Rendering…"
                : state.status === "ready"
                  ? `Generate next (${duration >= 180 ? "3min" : duration + "s"})`
                  : `Generate now (${duration >= 180 ? "3min" : duration + "s"})`}
            </button>

            <div className="text-[10px] text-white/40 font-mono ml-auto">
              {duration >= 90 && (
                <span className="text-emerald-300 mr-2">
                  📖 Long-form storytelling
                </span>
              )}
              ~${duration >= 180 ? "0.30" : duration >= 90 ? "0.15" : duration === 60 ? "0.10" : duration === 30 ? "0.05" : "0.03"} ·
              {" "}{duration >= 180 ? "3-4 min" : duration >= 90 ? "90-120s" : duration === 60 ? "90s" : duration === 30 ? "50s" : "30s"} render
            </div>
          </div>

          {/* Busy state — renders IMMEDIATELY below the Generate button
              so users see feedback without needing to scroll past the
              Batch + A/B panels. Also fixes the "I clicked Generate and
              nothing happened" perception when a cached hit finishes so
              fast the video was way below the fold. */}
          {busy && (
            <BusyIndicator
              label="Writing script · voicing · rendering MP4…"
              testId="admin-dnp-one-click-loading"
            />
          )}

          {/* Ready state — video preview + download + post buttons */}
          {state.status === "ready" && state.url && (
            <div
              className="mt-4 flex flex-col md:flex-row gap-4 rounded-xl bg-black/40 border border-emerald-500/20 p-3"
              data-testid="admin-dnp-one-click-ready"
            >
              <video
                src={state.cdnUrl || `${API_BASE}${state.url}`}
                controls
                playsInline
                data-testid="admin-dnp-one-click-player"
                className="w-40 sm:w-44 rounded-md bg-black border border-white/10"
                style={{ aspectRatio: "9/16" }}
              />
              <div className="flex-1 flex flex-col gap-2 text-[13px] text-white/70 min-w-0">
                <div className="text-white font-bold truncate" data-testid="admin-dnp-one-click-picked-topic">
                  {state.topicLabel || selectedTopic?.label || "Promo video"}
                </div>
                <div className="text-[11px] text-white/50">
                  Duration: {state.duration ? state.duration.toFixed(1) : duration}s ·
                  Voice: <b className="text-white">{voice}</b> ·
                  {state.cached ? " Cached" : " Freshly rendered"}
                  {state.cdnUrl && <span className="ml-2 text-emerald-300">· CDN</span>}
                </div>
                <div className="flex flex-wrap gap-2 mt-1">
                  <a
                    href={state.cdnUrl ? state.cdnUrl.replace("/upload/", "/upload/fl_attachment/") : `${API_BASE}${state.url}`}
                    download
                    data-testid="admin-dnp-one-click-download"
                    className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-bold bg-emerald-500 hover:bg-emerald-400 text-black transition-colors"
                  >
                    <Download size={12} weight="bold" /> Download MP4
                  </a>
                  {state.url && (
                    <a
                      href={`${API_BASE}/api/admin/dnp/tiktok-video/promo-crossplatform/${(state.url.match(/([a-f0-9]{16})\.mp4/) || [])[1]}.zip`}
                      download
                      data-testid="admin-dnp-one-click-crossplatform-zip"
                      className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-bold bg-white/10 hover:bg-white/20 text-white border border-emerald-500/30 transition-colors"
                      title="MP4 for TikTok + IG Reels + YT Shorts + .srt captions + posting guide"
                    >
                      <Download size={12} weight="bold" /> Cross-platform ZIP
                    </a>
                  )}
                  <PostToTikTokButton
                    mp4Url={state.cdnUrl || state.url}
                    defaultTitle={`${state.topicLabel || selectedTopic?.label || "FreightPilotAI"} — freightpilotai.ai #trucking #owneroperator #dispatch`}
                    testIdPrefix="admin-dnp-one-click"
                  />
                  <a
                    href="https://www.tiktok.com/upload"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="admin-dnp-one-click-upload-link"
                    className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-bold bg-white/10 hover:bg-white/20 text-white border border-white/10 transition-colors"
                  >
                    Upload manually →
                  </a>
                  <button
                    type="button"
                    onClick={() => run(true)}
                    disabled={busy}
                    data-testid="admin-dnp-one-click-regenerate"
                    className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-bold bg-white/10 hover:bg-white/20 text-white border border-white/10 transition-colors"
                  >
                    <ArrowClockwise size={12} weight="bold" /> Regenerate this one
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Error state */}
          {state.status === "error" && (
            <div
              className="mt-4 text-[12px] text-red-300 font-mono"
              data-testid="admin-dnp-one-click-error"
            >
              {state.error || "Generation failed — check backend logs."}
            </div>
          )}

          {/* Batch "Generate All 7" — all 7 promo topics as a ZIP */}
          <div
            className="mt-4 rounded-lg border border-emerald-500/25 bg-black/40 p-3"
            data-testid="admin-dnp-batch-panel"
          >
            <div className="flex flex-wrap items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold mb-0.5">
                  ⚡ Batch mode
                </div>
                <div className="text-sm font-bold text-white">
                  Generate all 7 promo topics at once
                </div>
                <div className="text-[11px] text-white/60 mt-0.5 leading-snug max-w-lg">
                  Renders all 7 promo videos at <b>{duration >= 180 ? "3min" : duration + "s"} / {voice}</b> and packages them into a single ZIP.
                  A full week of TikTok content in one click. Takes ~{duration >= 180 ? "18-25" : duration >= 90 ? "10-14" : duration === 60 ? "4-6" : duration === 30 ? "3-4" : "2-3"} min.
                </div>
              </div>
              <button
                type="button"
                onClick={() => runBatch(false)}
                disabled={busy || batchBusy}
                data-testid="admin-dnp-batch-generate"
                className="inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-bold bg-emerald-500 hover:bg-emerald-400 text-black transition-colors disabled:opacity-50 shrink-0"
              >
                <FilmSlate size={14} weight="bold" />
                {batchBusy ? "Rendering 7 videos…" : "Generate All 7"}
              </button>
            </div>

            {batchBusy && (
              <BusyIndicator
                label="Writing 7 scripts · voicing · rendering 7 MP4s · packaging ZIP… please wait 2-20 min."
                testId="admin-dnp-batch-loading"
              />
            )}

            {batchState.status === "ready" && batchState.zipUrl && (
              <div
                className="mt-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-3"
                data-testid="admin-dnp-batch-ready"
              >
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <div className="text-sm font-bold text-white">
                    ✅ {batchState.successCount}/{batchState.totalCount} videos ready
                  </div>
                  <a
                    href={`${API_BASE}${batchState.zipUrl}`}
                    download
                    data-testid="admin-dnp-batch-download-zip"
                    className="inline-flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-xs font-bold bg-emerald-500 hover:bg-emerald-400 text-black transition-colors ml-auto"
                  >
                    <Download size={12} weight="bold" /> Download all 7 (ZIP)
                  </a>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {batchState.results.map((r, i) => (
                    <div
                      key={r.topic}
                      className={`rounded px-2 py-1.5 text-[11px] font-mono border ${
                        r.error
                          ? "bg-red-500/10 border-red-500/30 text-red-300"
                          : "bg-black/40 border-white/10 text-white/80"
                      }`}
                      data-testid={`admin-dnp-batch-item-${r.topic}`}
                    >
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-white text-[11px] truncate">
                            {String(i + 1).padStart(2, "0")}. {r.topic_label || r.topic}
                          </div>
                          <div className="text-white/40 mt-0.5 truncate">
                            {r.error ? "❌ " + r.error.slice(0, 24) : `${(r.duration_sec || duration).toFixed(1)}s · ${r.cached ? "cached" : "fresh"}`}
                          </div>
                        </div>
                        {!r.error && r.mp4_url && (
                          <PostToTikTokButton
                            mp4Url={r.cdn_url || r.mp4_url}
                            defaultTitle={`${r.topic_label || "FreightPilotAI"} — freightpilotai.ai #trucking #owneroperator #dispatch`}
                            testIdPrefix={`admin-dnp-batch-${r.topic}`}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => runBatch(true)}
                  disabled={batchBusy}
                  data-testid="admin-dnp-batch-regenerate"
                  className="mt-3 inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[10px] font-bold bg-white/10 hover:bg-white/20 text-white border border-white/10 transition-colors"
                >
                  <ArrowClockwise size={10} weight="bold" /> Regenerate all fresh
                </button>
              </div>
            )}

            {batchState.status === "error" && (
              <div
                className="mt-3 text-[12px] text-red-300 font-mono"
                data-testid="admin-dnp-batch-error"
              >
                {batchState.error || "Batch failed — check backend logs."}
              </div>
            )}

            {/* Weekly auto-batch cron status + manual test-send */}
            <WeeklyAutoBatchStatus />

            {/* Face cameo — webcam clip corner overlay for new promo reels */}
            <FaceCameoCard />

            {/* Reel click leaderboard — which topics actually convert? */}
            <ReelLeaderboard />

            {/* Win Wall reel — 15s stitched from newest first-load cards, Friday auto-email */}
            <WinWallReelCard />

            {/* Paid vs free — Facebook ads next to reel results */}
            <TrafficSplitCard />
            <AdCostTrendCard />
            <VerdictHistoryCard />
            <ReelPostStreakCard />
            <WinReelQueueCard />
            <OfflineQRCard />

            {/* 30-day click heatmap — spot the best days to post */}
            <ReelClickHeatmap />
          </div>

          {/* ────── A/B Pair Panel — split-test 30s vs 90s founder-story ────── */}
          <div
            className="mt-4 rounded-lg border border-amber-500/25 bg-black/40 p-3"
            data-testid="admin-dnp-ab-panel"
          >
            <div className="flex flex-wrap items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">
                    🅰️🅱️ A/B split test
                  </span>
                  {captionDefault && (
                    <span
                      data-testid="admin-dnp-caption-default-badge"
                      className="inline-flex items-center gap-1 rounded-full bg-yellow-400/10 border border-yellow-400/40 px-2 py-0.5 text-[9px] font-bold text-yellow-300"
                      title="New reels render with this caption style unless you pick otherwise"
                    >
                      👑 Default: {captionDefault} captions
                    </span>
                  )}
                </div>
                <div className="text-sm font-bold text-white">
                  {abMode === "captions" ? "Classic vs boxed captions" : "30s punchy vs 90s storytelling"}
                </div>
                <div className="text-[11px] text-white/60 mt-0.5 leading-snug max-w-lg">
                  {abMode === "captions"
                    ? "Renders the SAME 30s promo twice — white-outline captions vs orange-pill captions — so you can test which caption style pulls more views."
                    : "Renders the SAME promo topic at both lengths so you can test which converts more signups."}{" "}
                  UTM tags auto-generated — paste them in your TikTok bio link and check GA4 in a week.
                </div>
              </div>
              <select
                value={abMode}
                onChange={(e) => setAbMode(e.target.value)}
                disabled={busy || batchBusy || abBusy}
                data-testid="admin-dnp-ab-mode"
                className="text-xs bg-black/50 border border-white/10 rounded px-2 py-1.5 text-white font-mono max-w-[170px]"
              >
                <option value="length">Test: 30s vs 90s</option>
                <option value="captions">Test: caption styles</option>
              </select>
              <select
                value={abTopic}
                onChange={(e) => setAbTopic(e.target.value)}
                disabled={busy || batchBusy || abBusy}
                data-testid="admin-dnp-ab-topic"
                className="text-xs bg-black/50 border border-white/10 rounded px-2 py-1.5 text-white font-mono max-w-[180px]"
              >
                {promoTopics.map((t) => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => runAb(false)}
                disabled={busy || batchBusy || abBusy}
                data-testid="admin-dnp-ab-generate"
                className="inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-bold bg-amber-500 hover:bg-amber-400 text-black transition-colors disabled:opacity-50 shrink-0"
              >
                <FilmSlate size={14} weight="bold" />
                {abBusy ? "Rendering pair…" : "Generate A/B pair"}
              </button>
            </div>

            {abBusy && (
              <BusyIndicator
                label="Rendering 30s + 90s in parallel · ~2-3 min wall clock…"
                testId="admin-dnp-ab-loading"
                accent="amber"
              />
            )}

            {abState.status === "ready" && (
              <div
                className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3"
                data-testid="admin-dnp-ab-ready"
              >
                {[
                  { key: "a", data: abState.a, label: abState.mode === "captions" ? "A · classic captions" : "A · 30s punchy", accent: "emerald" },
                  { key: "b", data: abState.b, label: abState.mode === "captions" ? "B · boxed captions" : "B · 90s long-form", accent: "amber" },
                ].map(({ key, data, label, accent }) => (
                  <div
                    key={key}
                    data-testid={`admin-dnp-ab-variant-${key}`}
                    className={`rounded-lg bg-black/40 border p-3 ${accent === "emerald" ? "border-emerald-500/30" : "border-amber-500/30"}`}
                  >
                    <div className={`text-[10px] uppercase tracking-widest font-bold mb-1 ${accent === "emerald" ? "text-emerald-300" : "text-amber-300"}`}>
                      {label}
                    </div>
                    {data?.mp4_url && (
                      <video
                        src={videoUrl(data)}
                        controls
                        playsInline
                        data-testid={`admin-dnp-ab-player-${key}`}
                        className="w-full max-w-[180px] rounded-md bg-black border border-white/10"
                        style={{ aspectRatio: "9/16" }}
                      />
                    )}
                    <div className="text-[11px] text-white/60 mt-2">
                      Duration: {data?.duration_sec ? data.duration_sec.toFixed(1) : "?"}s · Style: <b className="text-white">{data?.style}</b> · {data?.cached ? "Cached" : "Fresh"}
                    </div>
                    <div className="mt-2 text-[10px] font-mono text-white/50">
                      UTM: <span className={accent === "emerald" ? "text-emerald-300" : "text-amber-300"} data-testid={`admin-dnp-ab-utm-${key}`}>
                        {abState.utmHints?.[key]}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          const abLink = `https://freightpilotai.ai/${abState.utmHints?.[key] || ""}`;
                          navigator.clipboard.writeText(abLink);
                          rememberAngle(abLink);
                          toast.success(`Copied ${label} link`);
                        }}
                        data-testid={`admin-dnp-ab-copy-utm-${key}`}
                        className="ml-2 text-white/70 hover:text-white underline"
                      >
                        copy link
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {data?.mp4_url && (
                        <>
                          <a
                            href={downloadUrl(data)}
                            download
                            data-testid={`admin-dnp-ab-download-${key}`}
                            className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-[10px] font-bold ${accent === "emerald" ? "bg-emerald-500 hover:bg-emerald-400" : "bg-amber-500 hover:bg-amber-400"} text-black transition-colors`}
                          >
                            <Download size={10} weight="bold" /> MP4
                          </a>
                          <a
                            href={`${API_BASE}/api/admin/dnp/tiktok-video/promo-crossplatform/${(data.mp4_url.match(/([a-f0-9]{16})\.mp4/) || [])[1]}.zip`}
                            download
                            data-testid={`admin-dnp-ab-crossplatform-${key}`}
                            className="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-[10px] font-bold bg-white/10 hover:bg-white/20 text-white border border-white/10 transition-colors"
                          >
                            <Download size={10} weight="bold" /> Cross-platform ZIP
                          </a>
                          <PostToTikTokButton
                            mp4Url={data.cdn_url || data.mp4_url}
                            defaultTitle={`${abState.topicLabel} — freightpilotai.ai${abState.utmHints?.[key] || ""} #trucking #owneroperator`}
                            testIdPrefix={`admin-dnp-ab-${key}`}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(
                                promoCaption(abState.topicLabel, abState.utmHints?.[key])
                              );
                              toast.success(`Copied ${label} caption`);
                            }}
                            data-testid={`admin-dnp-ab-copy-caption-${key}`}
                            className="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-[10px] font-bold bg-white/10 hover:bg-white/20 text-white border border-white/10 transition-colors"
                          >
                            <Copy size={10} weight="bold" /> Copy caption
                          </button>
                          {abState.mode === "captions" && (
                            <button
                              type="button"
                              onClick={async () => {
                                const style = key === "a" ? "classic" : "boxed";
                                try {
                                  await client.post(`/admin/dnp/caption-style-default?style=${style}`);
                                  setCaptionDefault(style);
                                  toast.success(`Winner tagged — new reels default to ${style} captions 👑`);
                                } catch { toast.error("Couldn't save the default style"); }
                              }}
                              data-testid={`admin-dnp-ab-make-default-${key}`}
                              className="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-[10px] font-bold bg-yellow-400/90 hover:bg-yellow-300 text-black transition-colors"
                            >
                              👑 Make default
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {abState.status === "error" && (
              <div
                className="mt-3 text-[12px] text-red-300 font-mono"
                data-testid="admin-dnp-ab-error"
              >
                {abState.error || "A/B pair failed — check backend logs."}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


/* ─────────────────── TikTok Connect Panel ───────────────────
 *
 * Renders one of three states based on `/api/tiktok/status`:
 *   1. Not connected → big "Connect TikTok Account" button that POSTs to
 *      /api/tiktok/oauth/start and window-locations the browser to the
 *      returned authorize URL.
 *   2. Connected → account handle, scopes, "Sandbox" badge, "Disconnect".
 *   3. Loading → skeleton pill while we hit /tiktok/status.
 *
 * Also reads ?tiktok_connected=1 / ?tiktok_error=... on mount so the OAuth
 * callback redirect can hand feedback back to the founder.
 * */
function TikTokConnectPanel() {
  const [state, setState] = useState({ loading: true });
  const [busy, setBusy] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const r = await client.get("/tiktok/status");
      setState({ loading: false, ...r.data });
    } catch (e) {
      setState({ loading: false, error: e?.response?.data?.detail || "Failed to load TikTok status" });
    }
  }, []);

  useEffect(() => {
    loadStatus();

    // Handle OAuth callback redirect flags
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("tiktok_connected") === "1") {
        toast.success("TikTok account connected ✓");
        params.delete("tiktok_connected");
        const q = params.toString();
        window.history.replaceState({}, "", window.location.pathname + (q ? `?${q}` : ""));
      } else if (params.get("tiktok_error")) {
        toast.error(`TikTok connect failed: ${params.get("tiktok_error")}`);
        params.delete("tiktok_error");
        const q = params.toString();
        window.history.replaceState({}, "", window.location.pathname + (q ? `?${q}` : ""));
      }
    } catch { /* SSR safety, ignore */ }
  }, [loadStatus]);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await client.get("/tiktok/oauth/start");
      const url = r.data?.authorize_url;
      if (!url) throw new Error("No authorize_url returned");
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Failed to start TikTok OAuth");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect TikTok? You'll need to re-authorize before posting again.")) return;
    setBusy(true);
    try {
      await client.post("/tiktok/disconnect");
      toast.success("TikTok disconnected");
      await loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to disconnect");
    } finally {
      setBusy(false);
    }
  };

  if (state.loading) {
    return (
      <div
        className="mb-6 rounded-xl border border-white/10 bg-white/[0.02] p-4"
        data-testid="admin-tiktok-connect-loading"
      >
        <div className="text-[12px] text-white/40 font-mono">Loading TikTok connection…</div>
      </div>
    );
  }

  const connected = state.connected;
  const sandbox = state.sandbox;

  return (
    <div
      className={`mb-6 rounded-xl border p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${
        connected
          ? "border-pink-500/40 bg-gradient-to-br from-pink-500/[0.06] via-black/40 to-black/60"
          : "border-white/10 bg-white/[0.02]"
      }`}
      data-testid="admin-tiktok-connect-card"
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
        connected ? "bg-pink-500/20 text-pink-300" : "bg-white/5 text-white/50"
      }`}>
        <TiktokLogo size={22} weight="duotone" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-white flex items-center gap-2 flex-wrap" data-testid="admin-tiktok-connect-status">
          TikTok posting
          <span
            className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${
              connected
                ? "bg-pink-500/15 text-pink-300 border border-pink-500/40"
                : "bg-white/5 text-white/60 border border-white/10"
            }`}
            data-testid={connected ? "admin-tiktok-status-connected" : "admin-tiktok-status-disconnected"}
          >
            {connected ? "Connected" : "Not connected"}
          </span>
          {sandbox && (
            <span
              className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/40"
              title="TikTok Sandbox — posts are private to your own account until app approval"
              data-testid="admin-tiktok-sandbox-badge"
            >
              Sandbox
            </span>
          )}
        </div>
        <div className="text-[12px] text-neutral-400 mt-0.5 leading-snug">
          {connected ? (
            <>
              Posting to <b className="text-white font-mono">@{state.account}</b>
              {state.open_id && (
                <> · <span className="text-white/50 font-mono">open_id: {String(state.open_id).slice(0, 8)}…</span></>
              )}
              {sandbox && (
                <> · <span className="text-amber-300/80">Sandbox posts land as private (SELF_ONLY) — visible only to you until TikTok approves the app for production.</span></>
              )}
            </>
          ) : (
            <>Connect the <b className="text-white font-mono">@freightpilotai</b> TikTok account once — the "Post to TikTok" button on every generated video uploads directly via TikTok's Content Posting API. {sandbox && <span className="text-amber-300/80">Currently in Sandbox — posts will be private-only until TikTok approves the app.</span>}</>
          )}
        </div>
      </div>

      {connected ? (
        <button
          type="button"
          onClick={disconnect}
          disabled={busy}
          data-testid="admin-tiktok-disconnect-btn"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-md px-3.5 py-2 text-xs font-semibold bg-white/10 hover:bg-red-500 hover:text-white text-white transition-colors disabled:opacity-50 border border-white/10"
        >
          {busy ? "…" : "Disconnect"}
        </button>
      ) : (
        <button
          type="button"
          onClick={connect}
          disabled={busy}
          data-testid="admin-tiktok-connect-btn"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-bold bg-pink-500 hover:bg-pink-400 text-black transition-colors disabled:opacity-50"
        >
          <LinkSimple size={14} weight="bold" />
          {busy ? "Redirecting…" : "Connect TikTok"}
        </button>
      )}
    </div>
  );
}


/* ─────────────────── TikTok Auto-Post + Evening Queue panel ─────────────────── */
function TikTokAutoPostPanel() {
  const [settings, setSettings] = useState(null);
  const [queue, setQueue] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, q] = await Promise.all([
        client.get("/tiktok/settings"),
        client.get("/tiktok/queue"),
      ]);
      setSettings(s.data);
      setQueue(q.data);
    } catch { /* silent — nice-to-have panel */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleAutoPost = async () => {
    setBusy(true);
    try {
      const { data } = await client.post("/tiktok/settings", { auto_post: !settings.auto_post });
      setSettings(data);
      toast.success(data.auto_post
        ? "Auto-post ON — new reels queue themselves for the evening window"
        : "Auto-post off");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update setting");
    } finally {
      setBusy(false);
    }
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const { data } = await client.post("/tiktok/queue/run-now?force=true");
      const posted = data?.queue_run?.posted ?? 0;
      const reason = data?.queue_run?.reason;
      toast[posted > 0 ? "success" : "info"](
        posted > 0 ? `Posted ${posted} video${posted === 1 ? "" : "s"} to TikTok` : `Queue run: ${reason || "nothing due"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Queue run failed");
    } finally {
      setBusy(false);
    }
  };

  if (!settings) return null;
  return (
    <div className="mb-6 rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="tiktok-autopost-panel">
      <div className="flex items-center gap-3 flex-wrap">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={settings.auto_post}
            onChange={toggleAutoPost}
            disabled={busy}
            className="w-4 h-4 accent-pink-500 cursor-pointer"
            data-testid="tiktok-autopost-toggle"
          />
          <span className="text-sm font-semibold text-white">Auto-post new reels</span>
        </label>
        <span className="text-[11px] text-neutral-500">
          Evening window {settings.window_start}:00–{settings.window_end}:00 CT · max {settings.daily_cap}/day · AI captions included
        </span>
        <span className="text-[11px] px-2 py-0.5 rounded-full bg-pink-500/10 border border-pink-500/30 text-pink-300" data-testid="tiktok-queue-count">
          {queue?.queued_count ?? 0} queued
        </span>
        <button
          onClick={runNow}
          disabled={busy}
          data-testid="tiktok-queue-run-now"
          className="ml-auto text-[11px] font-bold px-3 py-1.5 rounded-md bg-white/5 border border-white/10 text-white/70 hover:text-white hover:border-white/25 transition-colors disabled:opacity-50"
        >
          {busy ? "Running…" : "Run queue now"}
        </button>
      </div>
      {queue?.queued?.length > 0 && (
        <div className="mt-3 space-y-1" data-testid="tiktok-queue-list">
          {queue.queued.slice(0, 5).map((q) => (
            <div key={q.id} className="flex items-center gap-2 text-[11px] text-neutral-400">
              <span className="text-pink-400">🕗</span>
              <span className="font-mono truncate max-w-[200px]">{q.filename}</span>
              <span className="truncate flex-1 text-neutral-500">{q.caption}</span>
              <button
                onClick={async () => {
                  try {
                    await client.delete(`/tiktok/queue/${q.id}`);
                    toast.success("Removed from queue");
                    load();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Remove failed");
                  }
                }}
                data-testid={`tiktok-queue-remove-${q.id}`}
                className="text-neutral-600 hover:text-red-400 transition-colors"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ─────────────────── Post to TikTok button ───────────────────
 *
 * Compact button that pushes an already-rendered MP4 to TikTok using
 * /api/tiktok/publish. Handles:
 *   - filename extraction from the mp4_url (e.g. /api/tiktok-videos/abc.mp4 → abc.mp4)
 *   - the "not connected yet" case (toast + hint to click Connect above)
 *   - polling /api/tiktok/publish-status/{id} every 3s until PUBLISH_COMPLETE or FAILED
 *   - inline status text below the button while posting
 * */
function PostToTikTokButton({ mp4Url, defaultTitle, testIdPrefix }) {
  const [phase, setPhase] = useState("idle"); // idle | posting | polling | done | failed
  const [publishId, setPublishId] = useState(null);
  const [statusText, setStatusText] = useState("");
  const [caption, setCaption] = useState("");
  const [capBusy, setCapBusy] = useState(false);
  const [queuedTonight, setQueuedTonight] = useState(false);

  const filename = (() => {
    try {
      const clean = String(mp4Url || "").split("?")[0];
      const parts = clean.split("/");
      return parts[parts.length - 1] || "";
    } catch {
      return "";
    }
  })();

  const post = async () => {
    if (!filename.endsWith(".mp4")) {
      toast.error("Can only post .mp4 files to TikTok");
      return;
    }
    setPhase("posting");
    setStatusText("Uploading to TikTok…");
    try {
      const r = await client.post("/tiktok/publish", {
        filename,
        title: caption || defaultTitle || "FreightPilot AI",
        privacy_level: "SELF_ONLY", // Sandbox forces this — direct-post rule
      }, { timeout: 300_000 });
      const pid = r.data?.publish_id;
      if (!pid) throw new Error("No publish_id returned");
      setPublishId(pid);
      setPhase("polling");
      setStatusText("TikTok is processing the video…");
      pollStatus(pid);
    } catch (e) {
      const err = e?.response?.data;
      let msg = "Post failed";
      if (typeof err === "string") {
        msg = err;
      } else if (err?.detail?.tiktok_error) {
        // TikTok API structured error
        const te = err.detail.tiktok_error?.error || err.detail.tiktok_error;
        msg = `TikTok: ${te.message || te.code || JSON.stringify(te)}`;
      } else if (err?.detail?.error) {
        msg = err.detail.error;
        if (err.detail.allowed) msg += ` (allowed: ${err.detail.allowed.join(", ")})`;
      } else if (err?.detail) {
        msg = typeof err.detail === "string" ? err.detail : JSON.stringify(err.detail);
      } else if (e?.message) {
        msg = e.message;
      }
      // Specific hint for the not-connected case
      if (String(msg).includes("not connected")) {
        msg = "TikTok not connected yet — click Connect TikTok above";
      }
      setPhase("failed");
      setStatusText(msg);
      toast.error(msg);
    }
  };

  const pollStatus = async (pid) => {
    let attempts = 0;
    const maxAttempts = 60; // 60 × 3s = 3 minutes cap
    const tick = async () => {
      attempts += 1;
      try {
        const r = await client.get(`/tiktok/publish-status/${encodeURIComponent(pid)}`);
        const s = r.data?.status || "PROCESSING";
        setStatusText(`TikTok status: ${s}`);
        if (s === "PUBLISH_COMPLETE") {
          setPhase("done");
          setStatusText("Posted ✓ Check @freightpilotai on TikTok");
          toast.success("Video posted to TikTok ✓");
          return;
        }
        if (s === "FAILED" || s === "PROCESSING_FAILED") {
          setPhase("failed");
          const reason = r.data?.fail_reason || r.data?.error?.message || "TikTok reported FAILED";
          setStatusText(reason);
          toast.error(reason);
          return;
        }
      } catch (e) {
        // Transient poll errors — log but keep trying
      }
      if (attempts >= maxAttempts) {
        setPhase("done");
        setStatusText("Upload accepted — check TikTok app to confirm publish");
        return;
      }
      setTimeout(tick, 3000);
    };
    setTimeout(tick, 2500);
  };

  const genCaption = async () => {
    setCapBusy(true);
    try {
      const r = await client.post("/tiktok/caption", { text: defaultTitle || filename });
      setCaption(r.data?.caption || "");
      toast.success("Caption ready ✨");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Caption generation failed");
    } finally {
      setCapBusy(false);
    }
  };

  const scheduleTonight = async () => {
    if (!filename.endsWith(".mp4")) { toast.error("Can only queue .mp4 files"); return; }
    try {
      const r = await client.post("/tiktok/queue", {
        filename,
        caption: caption || defaultTitle || "FreightPilot AI",
      });
      if (r.data?.queued === false) toast.info("Already in tonight's queue");
      else toast.success("Queued — posts in tonight's evening window 🕗");
      setQueuedTonight(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not queue post");
    }
  };

  const busy = phase === "posting" || phase === "polling";
  const isDone = phase === "done";
  const isFailed = phase === "failed";

  return (
    <div className="inline-flex flex-col gap-1">
      <button
        type="button"
        onClick={post}
        disabled={busy || isDone}
        data-testid={`${testIdPrefix}-post-tiktok-btn`}
        className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-bold transition-colors disabled:opacity-50 ${
          isDone
            ? "bg-emerald-500 text-black"
            : isFailed
              ? "bg-red-500/20 border border-red-500/40 text-red-200 hover:bg-red-500/30"
              : "bg-pink-500 hover:bg-pink-400 text-black"
        }`}
      >
        <PaperPlaneTilt size={12} weight="bold" />
        {isDone
          ? "Posted ✓"
          : busy
            ? phase === "posting" ? "Uploading…" : "Processing…"
            : isFailed
              ? "Retry post"
              : "Post to TikTok"}
      </button>
      {statusText && (
        <div
          className={`text-[10px] font-mono ${isFailed ? "text-red-300" : isDone ? "text-emerald-300" : "text-white/60"}`}
          data-testid={`${testIdPrefix}-post-tiktok-status`}
        >
          {statusText}
        </div>
      )}
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={genCaption}
          disabled={capBusy}
          data-testid={`${testIdPrefix}-caption-btn`}
          className="inline-flex items-center rounded-md px-2 py-1 text-[10px] font-bold bg-white/5 border border-white/10 text-white/70 hover:text-white hover:border-white/25 transition-colors disabled:opacity-50"
        >
          {capBusy ? "✨ Writing…" : "✨ Caption"}
        </button>
        <button
          type="button"
          onClick={scheduleTonight}
          disabled={queuedTonight}
          data-testid={`${testIdPrefix}-schedule-btn`}
          className={`inline-flex items-center rounded-md px-2 py-1 text-[10px] font-bold transition-colors disabled:opacity-60 ${
            queuedTonight
              ? "bg-emerald-500/15 border border-emerald-500/40 text-emerald-300"
              : "bg-white/5 border border-white/10 text-white/70 hover:text-white hover:border-white/25"
          }`}
        >
          {queuedTonight ? "Queued ✓" : "🕗 Tonight"}
        </button>
      </div>
      {caption && (
        <div className="text-[10px] text-white/50 max-w-[240px] leading-snug" data-testid={`${testIdPrefix}-caption-preview`}>
          {caption}
        </div>
      )}
    </div>
  );
}



/* ─────────────── Weekly Auto-Batch cron status ───────────────
 *
 * Renders inside the Batch panel. Shows the cron schedule + last few
 * runs from /api/admin/tiktok/weekly-batch/status, and offers a
 * "Send test email now" button that fires /run-now (force=true) so
 * the founder can verify the ZIP arrives without waiting until Sunday.
 * */
function WeeklyAutoBatchStatus() {
  const [status, setStatus] = useState(null);
  const [testing, setTesting] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const r = await client.get("/admin/tiktok/weekly-batch/status");
      setStatus(r.data);
    } catch (e) {
      // silent — this panel is a nice-to-have, don't spam the user
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const sendTest = async () => {
    if (!window.confirm(
      "Force a real batch send now?\n\n"
      + "• Renders all 7 videos (uses cached MP4s where available — usually fast)\n"
      + "• Emails the ZIP to the recipient below\n"
      + "• Marks this ISO week as \"sent\" (Sunday's auto-send will skip)\n\n"
      + "Continue?"
    )) return;
    setTesting(true);
    try {
      const r = await client.post("/admin/tiktok/weekly-batch/run-now", null, { timeout: 600_000 });
      const s = r.data?.status;
      if (s === "sent") {
        toast.success(`ZIP emailed to ${r.data.recipient} · ${r.data.success_count}/${r.data.total_count} rendered`);
      } else if (s === "skipped") {
        toast.info(`Already sent this week (${r.data.week_key})`);
      } else {
        toast.error(`Batch send failed: ${r.data?.reason || "unknown"}`);
      }
      await loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Send failed");
    } finally {
      setTesting(false);
    }
  };

  if (!status) return null;

  const lastRun = status.recent_runs?.[0];

  return (
    <div
      className="mt-3 rounded-lg border border-white/10 bg-black/40 p-3"
      data-testid="admin-dnp-weekly-cron-status"
    >
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-0.5 flex items-center gap-2">
            <ClockCounterClockwise size={11} weight="bold" />
            Weekly auto-send
            {status.enabled ? (
              <span className="text-emerald-400 text-[9px]">● ARMED</span>
            ) : (
              <span className="text-red-400 text-[9px]">● OFF</span>
            )}
          </div>
          <div className="text-[12px] text-white/70 leading-snug">
            Every <b className="text-white">{status.target.weekday} {String(status.target.hour_et).padStart(2, "0")}:00 ET</b> ·
            emails ZIP to <b className="text-white font-mono">{status.recipient}</b> ·
            {" "}{status.duration_sec}s · voice <span className="font-mono">{status.voice}</span>
          </div>
          {lastRun && (
            <div
              className={`text-[11px] font-mono mt-1 ${
                lastRun.status === "sent" ? "text-emerald-300"
                : lastRun.status === "failed" ? "text-red-300"
                : lastRun.status === "running" ? "text-amber-300"
                : "text-white/40"
              }`}
              data-testid="admin-dnp-weekly-cron-lastrun"
            >
              Last: {lastRun.week_key} · {lastRun.status}
              {lastRun.reason && <> · {lastRun.reason}</>}
              {lastRun.success_count !== undefined && lastRun.total_count !== undefined && (
                <> · {lastRun.success_count}/{lastRun.total_count} rendered</>
              )}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={sendTest}
          disabled={testing}
          data-testid="admin-dnp-weekly-cron-test-btn"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold bg-white/10 hover:bg-emerald-500 hover:text-black text-white transition-colors disabled:opacity-50 border border-white/10"
        >
          {testing ? "Sending…" : "Send test now"}
        </button>
      </div>
    </div>
  );
}

/**
 * ReelLeaderboard — which /watch reels get the most "Sign up free"
 * clicks? Read-only panel, refreshes on mount + every 60s.
 */
function TrafficSplitCard() {
  const [data, setData] = useState(null);
  const [breakEven, setBreakEven] = useState(null);
  const [heatTrend, setHeatTrend] = useState(null);
  const [coach, setCoach] = useState(null);
  const [spendAmt, setSpendAmt] = useState("");
  const [spendAngle, setSpendAngle] = useState("");
  const [angleOptions, setAngleOptions] = useState([]);
  const [spendDate, setSpendDate] = useState(new Date().toISOString().slice(0, 10));

  // Auto Angle Tags — prefill from the last ad link copied, offer the rest.
  useEffect(() => {
    let recent = [];
    try { recent = JSON.parse(localStorage.getItem("fp_recent_angles") || "[]"); } catch { /* noop */ }
    if (recent.length) {
      setAngleOptions(recent);
      setSpendAngle((cur) => cur || recent[0]);
    }
    client.get("/admin/source-roi").then((r) => {
      const extra = (r.data?.rows || []).map((x) => x.angle).filter((a) => a && !a.startsWith("("));
      setAngleOptions((prev) => [...new Set([...prev, ...extra])]);
    }).catch(() => {});
  }, []);
  const [savingSpend, setSavingSpend] = useState(false);

  const load = useCallback(() => {
    client.get("/admin/traffic-split?days=30").then((r) => setData(r.data)).catch(() => {});
    client.get("/admin/break-even").then((r) => setBreakEven(r.data)).catch(() => {});
    client.get("/admin/heat-trend").then((r) => setHeatTrend(r.data)).catch(() => {});
    client.get("/admin/ad-budget-coach").then((r) => setCoach(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveSpend = async () => {
    const amt = parseFloat(spendAmt);
    if (Number.isNaN(amt) || amt < 0) return toast.error("Enter a valid $ amount");
    setSavingSpend(true);
    try {
      await client.post("/admin/ad-spend", { date: spendDate, amount: amt, channel: "facebook", angle: spendAngle.trim() });
      toast.success(`Logged $${amt} ad spend for ${spendDate}${spendAngle.trim() ? ` (${spendAngle.trim()})` : ""}`);
      setSpendAmt("");
      setSpendAngle("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingSpend(false);
    }
  };

  if (!data) return null;
  const maxLeads = Math.max(1, ...data.rows.map((r) => r.leads));
  const colors = { facebook_paid: "#3b82f6", reels_free: "#FF5A1F", other: "#737373" };

  return (
    <div className="card-surface p-4 mt-4" data-testid="traffic-split-card">
      <div className="flex items-center justify-between mb-3">
        <span className="overline">PAID VS FREE · LAST 30 DAYS</span>
        <span className="text-[10px] text-neutral-500 font-mono">{data.total_leads} leads</span>
      </div>
      <div className="space-y-3">
        {data.rows.map((r) => (
          <div key={r.key} data-testid={`traffic-split-row-${r.key}`}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-neutral-200">{r.label}</span>
              <span className="font-mono text-neutral-400">
                {r.leads} lead{r.leads === 1 ? "" : "s"} · {r.signups} signup{r.signups === 1 ? "" : "s"} · {r.won} won
                {r.mrr > 0 && <span className="text-emerald-300"> · ${r.mrr.toLocaleString()}/mo</span>}
              </span>
            </div>
            <div className="h-2 rounded-full bg-white/5 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{ width: `${(r.leads / maxLeads) * 100}%`, background: colors[r.key] }}
              />
            </div>
            {r.key === "facebook_paid" && r.spend > 0 && (
              <div className="text-[10px] text-sky-300/80 font-mono mt-1" data-testid="fb-spend-stats">
                ${r.spend.toLocaleString()} spent
                {r.cost_per_lead != null && ` · $${r.cost_per_lead}/lead`}
                {r.cost_per_signup != null && ` · $${r.cost_per_signup}/signup`}
              </div>
            )}
          </div>
        ))}
      </div>
      {breakEven && breakEven.spend > 0 && (
        <div className="mt-3 pt-3 border-t border-white/5" data-testid="break-even-meter">
          <div className="flex items-center justify-between text-[11px] mb-1.5">
            <span className="text-neutral-400 font-semibold uppercase tracking-wider text-[10px]">Break-even · {breakEven.month}</span>
            {breakEven.surplus >= 0 ? (
              <span className="text-emerald-300 font-bold" data-testid="break-even-status">
                PAID FOR ITSELF · +${breakEven.surplus.toLocaleString()}/mo
              </span>
            ) : (
              <span className="text-sky-300 font-mono" data-testid="break-even-status">
                {Math.min(100, breakEven.pct || 0).toFixed(0)}% paid back
              </span>
            )}
          </div>
          <div className="h-2.5 rounded-full bg-white/5 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                (breakEven.pct || 0) >= 100 ? "bg-emerald-400" : "bg-sky-400"
              }`}
              style={{ width: `${Math.min(100, breakEven.pct || 0)}%` }}
            />
          </div>
          <div className="text-[10px] text-neutral-500 mt-1 font-mono" data-testid="break-even-detail">
            ${breakEven.mrr_won.toLocaleString()}/mo won from ads vs ${breakEven.spend.toLocaleString()} spent this month
          </div>
        </div>
      )}
      {heatTrend?.this_week_avg != null && (
        <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between" data-testid="heat-trend-line">
          <span className="text-neutral-400 font-semibold uppercase tracking-wider text-[10px]">Avg Lead Heat · This Week</span>
          <span className="text-xs font-mono" data-testid="heat-trend-value">
            <span className="text-white font-semibold">{heatTrend.this_week_avg}/100</span>
            {heatTrend.delta != null ? (
              heatTrend.delta >= 0 ? (
                <span className="text-emerald-300"> ▲ +{heatTrend.delta} vs last wk</span>
              ) : (
                <span className="text-red-300"> ▼ {heatTrend.delta} vs last wk</span>
              )
            ) : (
              <span className="text-neutral-500"> · no leads last week</span>
            )}
          </span>
        </div>
      )}
      {coach && (
        <div className="mt-3 pt-3 border-t border-white/5" data-testid="ad-budget-coach">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
            <span className="text-neutral-400 font-semibold uppercase tracking-wider text-[10px]">Ad Budget Coach</span>
            <div className="flex items-center gap-2">
              <span
                data-testid="ad-budget-coach-action"
                className={`rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase ${
                  { start: "border-sky-500/40 bg-sky-500/10 text-sky-300",
                    increase: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
                    hold: "border-amber-500/40 bg-amber-500/10 text-amber-300",
                    reduce: "border-red-500/40 bg-red-500/10 text-red-300" }[coach.action]
                }`}
              >
                {coach.action}
              </span>
              <span className="text-xs text-white font-mono font-semibold" data-testid="ad-budget-coach-amount">
                ${coach.suggested_weekly.toLocaleString()}/wk
              </span>
            </div>
          </div>
          <div className="text-[10px] text-neutral-500 leading-relaxed" data-testid="ad-budget-coach-reason">
            {coach.reason}
          </div>
        </div>
      )}
      <div className="mt-3 pt-3 border-t border-white/5 flex flex-wrap items-center gap-2">
        <span className="text-[11px] text-neutral-500">Log FB spend</span>
        <div className="flex items-center gap-1">
          <span className="text-xs text-sky-300">$</span>
          <input
            data-testid="ad-spend-amount-input"
            type="number"
            min="0"
            value={spendAmt}
            onChange={(e) => setSpendAmt(e.target.value)}
            placeholder="20"
            className="w-20 bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-xs text-white focus:outline-none focus:border-sky-400/60"
          />
        </div>
        <input
          data-testid="ad-spend-date-input"
          type="date"
          value={spendDate}
          onChange={(e) => setSpendDate(e.target.value)}
          className="bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-xs text-neutral-200 focus:outline-none focus:border-sky-400/60 [color-scheme:dark]"
        />
        <input
          data-testid="ad-spend-angle-input"
          type="text"
          value={spendAngle}
          onChange={(e) => setSpendAngle(e.target.value)}
          placeholder="angle tag (utm_content)"
          list="fp-angle-options"
          className="w-40 bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-xs text-white focus:outline-none focus:border-sky-400/60"
        />
        <datalist id="fp-angle-options">
          {angleOptions.map((a) => <option key={a} value={a} />)}
        </datalist>
        <button
          data-testid="ad-spend-save-btn"
          onClick={saveSpend}
          disabled={savingSpend || !spendAmt}
          className={`rounded-full px-3 py-1.5 text-xs border border-sky-500/40 text-sky-300 hover:border-sky-400 transition-colors ${savingSpend || !spendAmt ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          {savingSpend ? "Saving..." : "Save"}
        </button>
        <span className="text-[10px] text-neutral-600">so the FB row shows cost per signup</span>
      </div>
      <div className="text-[10px] text-neutral-500 mt-3">
        Facebook = paid_social UTMs + /lp landing pages · Reels = tiktok/reels UTMs + /watch landings.
      </div>
    </div>
  );
}

const QR_PRESETS = [
  { key: "truck_stop", label: "Truck stop flyer" },
  { key: "trailer", label: "Trailer decal" },
  { key: "business_card", label: "Business card" },
  { key: "event", label: "Event / expo" },
];

function VerdictHistoryCard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    client.get("/admin/ads-verdicts").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data || !data.count) return null;

  const pill = (verdict) => {
    if (verdict.startsWith("WORKING")) return "border-emerald-500/40 bg-emerald-500/10 text-emerald-300";
    if (verdict.startsWith("NEEDS")) return "border-amber-500/40 bg-amber-500/10 text-amber-300";
    return "border-red-500/40 bg-red-500/10 text-red-300";
  };

  return (
    <div className="card-surface p-4 mt-4" data-testid="verdict-history-card">
      <div className="overline mb-3">ADS VERDICT HISTORY · THE STORY SO FAR</div>
      <div className="space-y-1.5">
        {data.rows.map((r, i) => (
          <div
            key={`${r.anchor}-${r.week}-${i}`}
            data-testid={`verdict-row-${r.week}`}
            className="flex items-center gap-3 flex-wrap rounded-md border border-white/[0.06] bg-white/[0.02] px-3 py-2"
          >
            <span className="text-xs font-bold text-neutral-300 font-mono shrink-0">W{r.week}</span>
            <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-semibold ${pill(r.verdict)}`}>
              {r.verdict.split("—")[0].trim()}
            </span>
            <span className="text-[10px] text-neutral-500 font-mono">
              ${(r.stats?.spend ?? 0).toLocaleString()} spent · {r.stats?.leads ?? 0} leads · {r.stats?.signups ?? 0} signups
            </span>
            <span className="text-[10px] text-neutral-600 font-mono ml-auto">{String(r.created_at).slice(0, 10)}</span>
          </div>
        ))}
      </div>
      <div className="text-[10px] text-neutral-500 mt-2">
        One verdict per completed ad week — green streaks mean scale, red streaks mean rework.
      </div>
    </div>
  );
}

const QR_HOOKS = [
  "STILL CHASING BROKERS FOR YOUR MONEY?",
  "RUN YOUR FLEET FROM YOUR CAB",
  "GET PAID FASTER. HAUL MORE.",
];

const FRESH_HOOKS = [
  "WHO OWES YOU MONEY RIGHT NOW? WE KNOW.",
  "STOP RUNNING YOUR FLEET FROM A NOTEBOOK",
  "ONE APP. EVERY LOAD. EVERY DOLLAR.",
  "YOUR MONEY SHOWS UP BEFORE THE FUEL BILL",
];

const loadStoredHooks = () => {
  try {
    const raw = JSON.parse(localStorage.getItem("fp_qr_hooks_v1"));
    if (Array.isArray(raw) && raw.length && raw.every((h) => h.text && h.tag)) return raw;
  } catch { /* corrupt storage falls back to defaults */ }
  return QR_HOOKS.map((t, i) => ({ text: t, tag: `h${i}` }));
};

function OfflineQRCard() {
  const [preset, setPreset] = useState("truck_stop");
  const [imgUrl, setImgUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scans, setScans] = useState(null);
  const [hooks, setHooks] = useState(loadStoredHooks);
  const [hookIdx, setHookIdx] = useState(0);
  const [posterBusy, setPosterBusy] = useState(false);

  const downloadPoster = async () => {
    setPosterBusy(true);
    try {
      const sel = hooks[hookIdx];
      const r = await client.get(
        `/admin/qr-poster?label=${preset}&headline=${encodeURIComponent(sel.text)}&h=${sel.tag}`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `freightpilot-poster-${preset}-${sel.tag}.png`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Poster downloaded — scans will count under this headline");
    } catch {
      toast.error("Poster failed");
    } finally {
      setPosterBusy(false);
    }
  };

  useEffect(() => {
    client.get("/admin/qr-scans").then((r) => {
      setScans(r.data);
      const all = r.data?.hooks_all || {};
      setHooks((hs) => {
        let best = -1, bestN = 0;
        hs.forEach((h, i) => {
          const n = all[h.tag] || 0;
          if (n > bestN) { bestN = n; best = i; }
        });
        if (best >= 0) setHookIdx(best);
        return hs;
      });
    }).catch(() => {});
  }, []);

  const hookCounts = hooks.map((h) => scans?.hooks_all?.[h.tag] || 0);
  const winnerIdx = hookCounts.some((n) => n > 0)
    ? hookCounts.indexOf(Math.max(...hookCounts))
    : -1;
  const scansLast30 = (scans?.rows || []).reduce((a, r) => a + (r.last_30d || 0), 0);
  const isRetired = (h) =>
    scansLast30 > 0 &&
    !(scans?.hooks_30d?.[h.tag] > 0) &&
    !(h.swapped_at && Date.now() - new Date(h.swapped_at).getTime() < 30 * 864e5);
  const retiredIdx = hooks.findIndex(isRetired);
  const nextFresh = FRESH_HOOKS.find((t) => !hooks.some((h) => h.text === t));
  const swapHook = (i) => {
    if (!nextFresh) return;
    const maxTag = Math.max(...hooks.map((h) => parseInt(h.tag.slice(1), 10) || 0), 2);
    const next = hooks.map((h, j) =>
      (j === i ? { text: nextFresh, tag: `h${maxTag + 1}`, swapped_at: new Date().toISOString() } : h));
    setHooks(next);
    localStorage.setItem("fp_qr_hooks_v1", JSON.stringify(next));
    setHookIdx(i);
    toast.success("Fresh hook swapped in — new posters will track under it");
  };

  useEffect(() => {
    let revoked = false;
    setLoading(true);
    client
      .get(`/admin/qr-code?label=${preset}`, { responseType: "blob" })
      .then((r) => {
        if (revoked) return;
        setImgUrl((old) => {
          if (old) URL.revokeObjectURL(old);
          return URL.createObjectURL(r.data);
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    return () => { revoked = true; };
  }, [preset]);

  const taggedUrl = `https://freightpilotai.ai/api/qr/${preset}`;
  const scanFor = (key) => scans?.rows?.find((r) => r.label === key);

  return (
    <div className="card-surface p-4 mt-4" data-testid="offline-qr-card">
      <div className="overline mb-3">OFFLINE QR · TRUCK STOPS &amp; TRAILERS</div>
      <div className="flex items-start gap-4 flex-wrap">
        <div className="shrink-0 rounded-lg bg-white p-2" data-testid="offline-qr-image-wrap">
          {imgUrl ? (
            <img src={imgUrl} alt="Offline QR code" className="h-36 w-36" data-testid="offline-qr-image" />
          ) : (
            <div className="h-36 w-36 flex items-center justify-center text-xs text-neutral-400">
              {loading ? "…" : "QR"}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {QR_PRESETS.map((p) => (
              <button
                key={p.key}
                data-testid={`offline-qr-preset-${p.key}`}
                onClick={() => setPreset(p.key)}
                className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold transition-colors ${
                  preset === p.key
                    ? "border-[#FF5A1F]/60 bg-[#FF5A1F]/15 text-[#FF9764]"
                    : "border-white/10 text-neutral-400 hover:border-white/25"
                }`}
              >
                {p.label}
                {scanFor(p.key) && (
                  <span className="ml-1 text-emerald-300" data-testid={`offline-qr-scans-${p.key}`}>
                    · {scanFor(p.key).total} scan{scanFor(p.key).total === 1 ? "" : "s"}
                  </span>
                )}
              </button>
            ))}
          </div>
          <div className="text-[10px] text-neutral-500 font-mono break-all" data-testid="offline-qr-url">
            {taggedUrl}
          </div>
          <div className="flex gap-2">
            {imgUrl && (
              <a
                href={imgUrl}
                download={`freightpilot-qr-${preset}.png`}
                data-testid="offline-qr-download"
                className="inline-flex items-center gap-1 rounded-full border border-sky-500/40 px-3 py-1.5 text-[11px] font-semibold text-sky-300 hover:bg-sky-500/10 transition-colors"
              >
                <Download size={12} weight="bold" /> Download PNG
              </a>
            )}
            <button
              data-testid="offline-qr-copy-url"
              onClick={async () => { await navigator.clipboard.writeText(taggedUrl); rememberAngle(taggedUrl); toast.success("Tagged link copied"); }}
              className="inline-flex items-center gap-1 rounded-full border border-white/15 px-3 py-1.5 text-[11px] font-semibold text-neutral-300 hover:border-white/30 transition-colors"
            >
              <Copy size={12} weight="bold" /> Copy link
            </button>
          </div>
          <div className="pt-2 border-t border-white/5 space-y-2">
            <div className="text-[10px] uppercase tracking-wider text-neutral-400 font-semibold">
              Poster Maker · pick a hook{winnerIdx >= 0 && <span className="text-yellow-300 normal-case tracking-normal"> — winning headline preselected</span>}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {hooks.map((h, i) => (
                <button
                  key={h.tag}
                  data-testid={`poster-hook-${i}`}
                  onClick={() => setHookIdx(i)}
                  className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold transition-colors ${
                    hookIdx === i
                      ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300"
                      : "border-white/10 text-neutral-400 hover:border-white/25"
                  }`}
                >
                  {h.text}
                  {scans?.hooks_all?.[h.tag] > 0 && (
                    <span className="ml-1 text-sky-300" data-testid={`poster-hook-scans-${i}`}>
                      · {scans.hooks_all[h.tag]} scan{scans.hooks_all[h.tag] === 1 ? "" : "s"}
                    </span>
                  )}
                  {winnerIdx === i && (
                    <span className="ml-1 text-yellow-300 font-bold" data-testid={`poster-hook-winner-${i}`}>
                      🏆 winning
                    </span>
                  )}
                  {isRetired(h) && (
                    <span className="ml-1 text-amber-300" data-testid={`poster-hook-retire-${i}`}>
                      · 0 scans 30d
                    </span>
                  )}
                </button>
              ))}
            </div>
            {retiredIdx >= 0 && nextFresh && (
              <div className="flex items-center gap-2 flex-wrap text-[10px]" data-testid="hook-retire-suggestion">
                <span className="text-amber-300">
                  "{hooks[retiredIdx].text}" pulled 0 scans in 30 days — retire it?
                </span>
                <button
                  data-testid="hook-swap-btn"
                  onClick={() => swapHook(retiredIdx)}
                  className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 font-semibold text-amber-300 hover:bg-amber-500/20 transition-colors"
                >
                  Swap in: "{nextFresh}"
                </button>
              </div>
            )}
            <button
              data-testid="poster-download-btn"
              onClick={downloadPoster}
              disabled={posterBusy}
              className={`inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-[11px] font-semibold text-emerald-300 hover:bg-emerald-500/20 transition-colors ${posterBusy ? "opacity-50" : ""}`}
            >
              <FilmSlate size={12} weight="bold" /> {posterBusy ? "Building…" : "Download print-ready poster"}
            </button>
          </div>
          <div className="text-[10px] text-neutral-500">
            Print it on flyers, trailer decals, or cards — every scan is counted per location
            {scans && scans.total_scans > 0 && (
              <span className="text-emerald-300" data-testid="offline-qr-total-scans"> ({scans.total_scans} total scans so far)</span>
            )}, and anyone who fills the form gets tagged
            <span className="text-neutral-300"> offline / qr / {preset}</span>.
          </div>
        </div>
      </div>
    </div>
  );
}

function WinReelQueueCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    client.get("/admin/win-reels").then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data || (!data.queued.length && !data.posted.length)) return null;

  const copyCaption = async (r) => {
    if (!r.caption) return toast.error("No caption on this reel yet");
    await navigator.clipboard.writeText(r.caption);
    toast.success("Caption copied — paste into TikTok");
  };
  const markPosted = async (r) => {
    setBusy(r.id);
    try {
      await client.post(`/admin/win-reels/${r.id}/mark-posted`);
      toast.success("Nice — win reel marked posted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="card-surface p-4 mt-4" data-testid="win-reel-queue-card">
      <div className="flex items-center justify-between mb-3">
        <span className="overline">WIN REEL QUEUE · POST THE MOMENTUM</span>
        <span className="text-[10px] text-neutral-500 font-mono" data-testid="win-reel-queue-counts">
          {data.queued.length} to post · {data.posted.length} posted
        </span>
      </div>
      {data.queued.length === 0 ? (
        <div className="text-xs text-neutral-500" data-testid="win-reel-queue-empty">
          Queue clear — every win reel is posted. Next closed deal auto-renders a fresh one.
        </div>
      ) : (
        <div className="space-y-2">
          {data.queued.map((r) => (
            <div
              key={r.id}
              data-testid={`win-reel-row-${r.id}`}
              className="flex items-center gap-2 flex-wrap rounded-md border border-emerald-500/20 bg-emerald-500/[0.04] px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium text-neutral-200 truncate">
                  Win: {r.company || "a new fleet"}
                </div>
                <div className="text-[10px] text-neutral-500 font-mono">
                  rendered {String(r.created_at).slice(0, 10)}
                </div>
              </div>
              <button
                data-testid={`win-reel-copy-caption-${r.id}`}
                onClick={() => copyCaption(r)}
                className="inline-flex items-center gap-1 rounded-full border border-white/15 px-2.5 py-1 text-[10px] font-semibold text-neutral-300 hover:border-white/30 transition-colors"
              >
                <Copy size={11} weight="bold" /> Caption
              </button>
              {(r.cdn_url || r.mp4_url) && (
                <a
                  href={r.cdn_url || r.mp4_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`win-reel-download-${r.id}`}
                  className="inline-flex items-center gap-1 rounded-full border border-sky-500/40 px-2.5 py-1 text-[10px] font-semibold text-sky-300 hover:bg-sky-500/10 transition-colors"
                >
                  <Download size={11} weight="bold" /> Video
                </a>
              )}
              <button
                data-testid={`win-reel-mark-posted-${r.id}`}
                onClick={() => markPosted(r)}
                disabled={busy === r.id}
                className={`inline-flex items-center gap-1 rounded-full border border-emerald-500/40 px-2.5 py-1 text-[10px] font-semibold text-emerald-300 hover:bg-emerald-500/10 transition-colors ${busy === r.id ? "opacity-50" : ""}`}
              >
                <CheckCircle size={11} weight="bold" /> {busy === r.id ? "..." : "Mark posted"}
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="text-[10px] text-neutral-500 mt-3">
        Every closed deal auto-renders a braggy reel with caption ready — copy, post, mark done.
      </div>
    </div>
  );
}

function AdCostTrendCard() {
  const [trend, setTrend] = useState(null);

  useEffect(() => {
    client.get("/admin/ad-cost-trend?weeks=8").then((r) => setTrend(r.data)).catch(() => {});
  }, []);

  if (!trend || !trend.has_data) return null;

  return (
    <div className="card-surface p-4 mt-4" data-testid="ad-cost-trend-card">
      <div className="flex items-center justify-between mb-3">
        <span className="overline">AD COST TREND · LAST {trend.weeks} WEEKS</span>
        <span className="text-[10px] text-neutral-500 font-mono">
          <span className="text-sky-300">■</span> spend · <span className="text-[#FF9764]">●</span> $/lead · <span className="text-emerald-300">●</span> $/signup
        </span>
      </div>
      <div style={{ width: "100%", height: 190 }}>
        <ResponsiveContainer>
          <ComposedChart data={trend.rows} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
            <XAxis dataKey="week" tick={{ fill: "#737373", fontSize: 10 }} axisLine={{ stroke: "#ffffff1a" }} tickLine={false} />
            <YAxis tick={{ fill: "#737373", fontSize: 10 }} axisLine={false} tickLine={false} />
            <Tooltip
              contentStyle={{ background: "#141414", border: "1px solid #ffffff1f", borderRadius: 8, fontSize: 11 }}
              labelStyle={{ color: "#e5e5e5" }}
              formatter={(v, name) => [v == null ? "—" : `$${v}`, name]}
            />
            <Bar dataKey="spend" name="Spend" fill="#3b82f6" opacity={0.55} radius={[3, 3, 0, 0]} />
            <Line type="monotone" dataKey="cost_per_lead" name="Cost / lead" stroke="#FF5A1F" strokeWidth={2} dot={{ r: 3, fill: "#FF5A1F" }} connectNulls />
            <Line type="monotone" dataKey="cost_per_signup" name="Cost / signup" stroke="#34d399" strokeWidth={2} dot={{ r: 3, fill: "#34d399" }} connectNulls />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="text-[10px] text-neutral-500 mt-2">
        Falling orange/green lines = Meta's learning phase working — your leads are getting cheaper.
      </div>
    </div>
  );
}

function ReelPostStreakCard() {
  const [streak, setStreak] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    client.get("/admin/reel-post-streak").then((r) => setStreak(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const logPost = async () => {
    setBusy(true);
    try {
      await client.post("/admin/reel-post-streak/log");
      toast.success("Logged — streak alive!");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (!streak) return null;
  const hot = streak.streak_weeks > 0;
  return (
    <div className="card-surface p-4 mt-4" data-testid="reel-post-streak-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <span className={`text-2xl font-bold font-mono ${hot ? "text-[#FF9764]" : "text-neutral-500"}`} data-testid="reel-post-streak-count">
            {streak.streak_weeks}
          </span>
          <div>
            <div className="overline">REEL POSTING STREAK</div>
            <div className="text-[11px] text-neutral-400 mt-0.5" data-testid="reel-post-streak-status">
              {streak.this_week_posted
                ? "Posted this week — streak safe ✓"
                : hot
                  ? "Post a reel this week to keep the streak alive"
                  : "Post one reel this week to start your streak"}
            </div>
          </div>
        </div>
        {!streak.this_week_posted && (
          <button
            data-testid="reel-post-streak-log-btn"
            onClick={logPost}
            disabled={busy}
            className={`rounded-full border border-[#FF5A1F]/50 bg-[#FF5A1F]/10 px-3 py-1.5 text-[11px] font-semibold text-[#FF9764] hover:bg-[#FF5A1F]/20 transition-colors ${busy ? "opacity-50" : ""}`}
          >
            {busy ? "..." : "I posted a reel"}
          </button>
        )}
      </div>
      <div className="text-[10px] text-neutral-500 mt-2">
        Counts TikTok posts you add, win reels marked posted, and manual logs — consecutive weeks.
      </div>
    </div>
  );
}

function ReelLeaderboard() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalViews, setTotalViews] = useState(0);
  const [totalLeads, setTotalLeads] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [captionBusy, setCaptionBusy] = useState(null);

  const copyCaption = async (reelId, force = false) => {
    setCaptionBusy(reelId);
    try {
      const { data } = await client.post(`/admin/dnp/reel-caption?cache_key=${encodeURIComponent(reelId)}${force ? "&force=true" : ""}`);
      await navigator.clipboard.writeText(data.full_text);
      toast.success(force ? "Fresh caption rolled + copied" : "Caption + hashtags copied — paste into TikTok");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Caption failed");
    } finally {
      setCaptionBusy(null);
    }
  };

  const load = useCallback(async () => {
    try {
      const res = await client.get("/reels/stats?limit=8");
      setRows(res.data?.leaderboard || []);
      setTotal(res.data?.total_events || 0);
      setTotalViews(res.data?.total_views || 0);
      setTotalLeads(res.data?.total_leads || 0);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div
      className="mt-4 rounded-lg border border-emerald-500/25 bg-black/40 p-3"
      data-testid="admin-dnp-reel-leaderboard"
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold mb-0.5">
            📈 /watch Leaderboard
          </div>
          <div className="text-[12px] text-white/60">
            Ranked by <b className="text-[#FF9764]">leads generated</b>, then clicks — double down on what converts.
            Auto-refreshes every 60s ·{" "}
            <span className="font-mono text-[#FF9764]" data-testid="admin-dnp-reel-total-leads">
              {totalLeads} lead{totalLeads === 1 ? "" : "s"}
            </span>
            {" · "}
            <span className="font-mono text-emerald-300" data-testid="admin-dnp-reel-total-clicks">
              {total} lifetime clicks
            </span>
            {totalViews > 0 && (
              <span className="ml-2 font-mono text-white/50" data-testid="admin-dnp-reel-total-views">
                · {totalViews} views
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="admin-dnp-reel-leaderboard-refresh"
          className="shrink-0 inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-bold bg-white/10 hover:bg-white/20 text-white border border-white/10 transition-colors"
        >
          <ArrowClockwise size={10} weight="bold" /> Refresh
        </button>
      </div>

      {loading && (
        <div className="text-[11px] text-white/50 font-mono" data-testid="admin-dnp-reel-leaderboard-loading">
          Loading…
        </div>
      )}

      {err && !loading && (
        <div className="text-[11px] text-red-300 font-mono" data-testid="admin-dnp-reel-leaderboard-error">
          {err}
        </div>
      )}

      {!loading && !err && rows.length === 0 && (
        <div className="text-[11px] text-white/40 font-mono py-2" data-testid="admin-dnp-reel-leaderboard-empty">
          No clicks tracked yet — send /watch traffic and this fills in.
        </div>
      )}

      {!loading && !err && rows.length > 0 && (
        <div className="space-y-1.5" data-testid="admin-dnp-reel-leaderboard-list">
          {rows.map((r, i) => (
            <div
              key={r.reel_id}
              data-testid={`admin-dnp-reel-leaderboard-row-${r.reel_id}`}
              className="flex items-center gap-2 rounded-md bg-white/[0.03] border border-white/5 px-2 py-1.5 text-[11px]"
            >
              <div className="w-5 text-center font-bold text-white/50 shrink-0">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white font-bold truncate">
                  {r.topic_label || r.topic || r.reel_id.slice(0, 12)}
                  {r.duration_sec && (
                    <span className="ml-1.5 text-[10px] text-white/40 font-mono">· {r.duration_sec}s</span>
                  )}
                </div>
                <div className="text-[10px] text-white/40 font-mono truncate">
                  {r.reel_id.slice(0, 10)}… · last click{" "}
                  {r.last_click_at ? new Date(r.last_click_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}
                </div>
              </div>
              {/* 7-day click trend sparkline */}
              {Array.isArray(r.daily_clicks) && r.daily_clicks.some((v) => v > 0) && (
                <Sparkline
                  data={r.daily_clicks}
                  testId={`admin-dnp-reel-leaderboard-sparkline-${r.reel_id}`}
                />
              )}
              {/* Watch-through % (avg pct of video watched, across viewers) */}
              {r.view_count > 0 && (
                <div
                  className="shrink-0 rounded-md bg-white/5 border border-white/10 text-white/80 font-mono px-2 py-0.5 text-[11px] flex flex-col items-end leading-tight"
                  title={`${r.view_count} views · avg watch-through ${r.avg_watch_pct}%`}
                  data-testid={`admin-dnp-reel-leaderboard-watchpct-${r.reel_id}`}
                >
                  <span className={r.avg_watch_pct >= 50 ? "text-emerald-300" : r.avg_watch_pct >= 25 ? "text-amber-300" : "text-white/60"}>
                    {r.avg_watch_pct}%
                  </span>
                  <span className="text-[9px] text-white/40">{r.view_count} views</span>
                </div>
              )}
              <div
                className="shrink-0 rounded-md bg-[#FF5A1F]/15 border border-[#FF5A1F]/40 text-[#FF9764] font-mono font-bold px-2 py-0.5 text-[11px] flex flex-col items-center leading-tight"
                title={`${r.leads_count || 0} signup lead${(r.leads_count || 0) === 1 ? "" : "s"} attributed to this reel`}
                data-testid={`admin-dnp-reel-leaderboard-leads-${r.reel_id}`}
              >
                <span>{r.leads_count || 0}</span>
                <span className="text-[8px] text-white/40 font-normal">leads</span>
              </div>
              <div
                className="shrink-0 rounded-md bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 font-mono font-bold px-2 py-0.5 text-[11px]"
                data-testid={`admin-dnp-reel-leaderboard-clicks-${r.reel_id}`}
              >
                {r.total_clicks}
              </div>
              <button
                type="button"
                onClick={async () => {
                  try {
                    const res = await client.post(`/reels/alerts/reset?reel_id=${encodeURIComponent(r.reel_id)}`);
                    if (res.data?.was_armed) {
                      toast.success("Alerts re-armed — next milestone will re-fire Slack");
                    } else {
                      toast("Alerts already un-fired — nothing to reset", { icon: "ℹ" });
                    }
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Reset failed");
                  }
                }}
                data-testid={`admin-dnp-reel-leaderboard-reset-${r.reel_id}`}
                title="Re-arm Slack alerts for this reel (100 clicks + 75% watch)"
                className="shrink-0 inline-flex items-center justify-center rounded-md px-1.5 py-1 text-white/40 hover:text-[#FF9764] hover:bg-white/5 transition-colors"
              >
                <BellRinging size={12} weight="bold" />
              </button>
              <button
                type="button"
                onClick={() => copyCaption(r.reel_id)}
                disabled={captionBusy === r.reel_id}
                data-testid={`admin-dnp-reel-caption-${r.reel_id}`}
                title="Copy ready-to-paste TikTok caption + hashtags"
                className={`shrink-0 inline-flex items-center justify-center rounded-md px-1.5 py-1 text-white/40 hover:text-[#FF9764] hover:bg-white/5 transition-colors ${captionBusy === r.reel_id ? "animate-pulse text-[#FF9764]" : ""}`}
              >
                <Copy size={12} weight="bold" />
              </button>
              <button
                type="button"
                onClick={() => copyCaption(r.reel_id, true)}
                disabled={captionBusy === r.reel_id}
                data-testid={`admin-dnp-reel-caption-reroll-${r.reel_id}`}
                title="Don't like the caption? Reroll a fresh one"
                className={`shrink-0 inline-flex items-center justify-center rounded-md px-1.5 py-1 text-white/40 hover:text-[#FF9764] hover:bg-white/5 transition-colors ${captionBusy === r.reel_id ? "animate-pulse text-[#FF9764]" : ""}`}
              >
                <ArrowClockwise size={12} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Sparkline — tiny 60×22 SVG line chart of the last 7 daily counts.
 *  Highlights the last point with an orange dot so today's number
 *  jumps out. Silent-hides when all data is zero (caller guards). */
function Sparkline(props) {
  const { data, testId } = props;
  const w = 60;
  const h = 22;
  const max = Math.max(1, ...data);
  const step = data.length > 1 ? w / (data.length - 1) : 0;
  const points = data.map((v, i) => {
    const x = i * step;
    const y = h - (v / max) * (h - 4) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const last = data[data.length - 1] || 0;
  const lastX = (data.length - 1) * step;
  const lastY = h - (last / max) * (h - 4) - 2;
  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      data-testid={testId}
      className="shrink-0"
      role="img"
      aria-label={`7-day clicks: ${data.join(", ")}`}
    >
      <title>{`Last 7 days: ${data.join(" · ")}`}</title>
      <polyline
        points={points}
        fill="none"
        stroke="#10B981"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={lastX} cy={lastY} r="2.5" fill="#FF5A1F" />
    </svg>
  );
}

/**
 * FaceCameoCard — upload a 10-20s webcam clip that loops in the corner of
 * every newly generated promo reel. Chunked upload sidesteps proxy limits.
 */
function FaceCameoCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [applyJob, setApplyJob] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    client.get("/admin/cameo/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const pollApply = useCallback(async () => {
    try {
      const { data } = await client.get("/admin/cameo/apply-status");
      setApplyJob(data.job);
      return data.job;
    } catch { return null; }
  }, []);
  useEffect(() => { pollApply(); }, [pollApply]);
  useEffect(() => {
    if (applyJob?.status !== "running") return;
    const t = setInterval(async () => {
      const j = await pollApply();
      if (j && j.status !== "running") {
        clearInterval(t);
        toast.success(`Cameo added to ${j.applied} reel${j.applied === 1 ? "" : "s"}`);
      }
    }, 3000);
    return () => clearInterval(t);
  }, [applyJob?.status, pollApply]);

  const applyToAll = async () => {
    try {
      const { data } = await client.post("/admin/cameo/apply-to-all");
      if (data.already_running) return toast.info("Already applying — hang tight");
      if (data.total === 0) return toast.success("All existing reels already have your cameo");
      setApplyJob({ status: "running", total: data.total, done: 0, applied: 0 });
      toast.success(`Adding your cameo to ${data.total} existing reel${data.total === 1 ? "" : "s"}...`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start — is the cameo uploaded and On?");
    }
  };

  const saveOverlaySetting = async (patch) => {
    try {
      const { data } = await client.post("/admin/cameo/settings", patch);
      setStatus((s) => ({ ...s, position: data.position, size: data.size }));
      toast.success(`Cameo ${patch.position ? "position" : "size"} saved — applies to future renders`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 120 * 1024 * 1024) {
      toast.error("Keep the clip under 120MB");
      return;
    }
    const chunkSize = 4 * 1024 * 1024;
    const total = Math.ceil(f.size / chunkSize);
    const uploadId = `cameo_${Date.now()}`;
    setBusy(true);
    setProgress(1);
    try {
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", String(i));
        fd.append("file", f.slice(i * chunkSize, (i + 1) * chunkSize));
        await client.post("/admin/cameo/upload-chunk", fd);
        setProgress(Math.round(((i + 1) / total) * 90));
      }
      await client.post("/admin/cameo/finalize", { upload_id: uploadId });
      setProgress(100);
      toast.success("Cameo uploaded + enabled — it'll appear on every newly generated reel");
      load();
    } catch (err2) {
      toast.error(err2?.response?.data?.detail || "Upload failed — try again");
    } finally {
      setBusy(false);
      setTimeout(() => setProgress(null), 900);
    }
  };

  const toggle = async () => {
    try {
      const res = await client.post("/admin/cameo/toggle", { enabled: !status?.enabled });
      toast.success(res.data.enabled ? "Cameo ON — new renders get the overlay" : "Cameo off");
      load();
    } catch (err2) {
      toast.error(err2?.response?.data?.detail || "Toggle failed");
    }
  };

  const remove = async () => {
    try {
      await client.delete("/admin/cameo");
      toast.success("Cameo clip removed");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div
      className="mb-4 rounded-xl border border-white/10 bg-white/[0.02] p-4"
      data-testid="admin-dnp-cameo-card"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${status?.enabled ? "bg-emerald-500/15 text-emerald-400" : "bg-white/5 text-white/50"}`}>
          <VideoCamera size={22} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white flex items-center gap-2 flex-wrap">
            Face Cameo
            <span
              data-testid="cameo-status"
              className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${
                status?.enabled
                  ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"
                  : status?.configured
                  ? "bg-white/5 text-white/50 border border-white/10"
                  : "bg-amber-500/15 text-amber-300 border border-amber-500/40"
              }`}
            >
              {status?.enabled ? "On" : status?.configured ? "Off" : "Not set"}
            </span>
          </div>
          <div className="text-[12px] text-neutral-400 mt-0.5 leading-snug">
            {status?.configured ? (
              <>Your {status.duration_sec ? `${Math.round(status.duration_sec)}s ` : ""}clip loops bottom-right on every <b className="text-white">newly generated</b> promo reel. Regenerate a topic with force to add it to existing videos.</>
            ) : (
              <>Upload a 10–20s webcam clip of yourself talking — it loops in the corner of every new promo reel so viewers see a real human behind the brand.</>
            )}
          </div>
          {progress !== null && (
            <div className="mt-2 h-1.5 w-full max-w-xs rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-[#FF5A1F] rounded-full transition-all" style={{ width: `${progress}%` }} />
            </div>
          )}
        </div>
        <div className="shrink-0 flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="video/mp4,video/webm,video/quicktime"
            className="hidden"
            onChange={onFile}
            data-testid="cameo-upload-input"
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            data-testid="cameo-upload-btn"
            className="inline-flex items-center gap-1.5 rounded-md px-3.5 py-2 text-xs font-semibold bg-white/10 hover:bg-[#FF5A1F] hover:text-black text-white transition-colors disabled:opacity-50 border border-white/10"
          >
            {busy ? "Uploading…" : status?.configured ? "Replace clip" : "Upload clip"}
          </button>
          {status?.configured && (
            <>
              <button
                type="button"
                onClick={toggle}
                data-testid="cameo-toggle"
                className={`rounded-md px-3 py-2 text-xs font-semibold border transition-colors ${status.enabled ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/25" : "bg-white/5 text-white/60 border-white/10 hover:bg-white/10"}`}
              >
                {status.enabled ? "On" : "Off"}
              </button>
              <button
                type="button"
                onClick={applyToAll}
                disabled={applyJob?.status === "running" || !status.enabled}
                data-testid="cameo-apply-all-btn"
                title={status.enabled ? "Burn your cameo into every existing reel" : "Turn the cameo On first"}
                className="rounded-md px-3 py-2 text-xs font-semibold border border-[#FF5A1F]/40 text-[#FF9764] hover:bg-[#FF5A1F]/15 transition-colors disabled:opacity-50"
              >
                {applyJob?.status === "running"
                  ? `Applying ${applyJob.done ?? 0}/${applyJob.total ?? "…"}`
                  : "Add to existing reels"}
              </button>
              <button
                type="button"
                onClick={remove}
                data-testid="cameo-delete"
                title="Remove the cameo clip"
                className="rounded-md p-2 text-white/40 hover:text-red-300 hover:bg-red-500/10 border border-white/10 transition-colors"
              >
                <Trash size={14} weight="bold" />
              </button>
              <select
                data-testid="cameo-position-select"
                value={status.position || "bottom-right"}
                onChange={(e) => saveOverlaySetting({ position: e.target.value })}
                title="Corner where your cameo appears"
                className="rounded-md px-2 py-2 text-xs bg-black/40 border border-white/10 text-neutral-200 focus:outline-none focus:border-[#FF5A1F]/60"
              >
                <option value="bottom-right">Bottom right</option>
                <option value="bottom-left">Bottom left</option>
                <option value="top-right">Top right</option>
                <option value="top-left">Top left</option>
              </select>
              <select
                data-testid="cameo-size-select"
                value={status.size || "medium"}
                onChange={(e) => saveOverlaySetting({ size: e.target.value })}
                title="Cameo size on the reel"
                className="rounded-md px-2 py-2 text-xs bg-black/40 border border-white/10 text-neutral-200 focus:outline-none focus:border-[#FF5A1F]/60"
              >
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
              </select>
            </>
          )}
        </div>
      </div>
      {status?.configured && status?.cloudinary_url && (
        <video
          src={status.cloudinary_url}
          controls
          muted
          className="mt-3 h-36 rounded-lg border border-white/10"
          data-testid="cameo-preview"
        />
      )}
    </div>
  );
}

/**
 * ReelClickHeatmap — GitHub-style 30-day calendar of reel clicks +
 * weekday totals so the founder can spot the best days to post.
 * Includes a "Email me this report" button firing the weekly digest now.
 */
function ReelClickHeatmap() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [sending, setSending] = useState(false);
  const [lastDigest, setLastDigest] = useState(null);

  useEffect(() => {
    client
      .get("/reels/heatmap?days=30")
      .then((res) => setData(res.data))
      .catch((e) => setErr(e?.response?.data?.detail || e?.message || "Failed to load"));
    client
      .get("/admin/reels-digest/history")
      .then((res) => setLastDigest((res.data?.digests || [])[0] || null))
      .catch(() => {});
  }, []);

  const sendDigest = async () => {
    setSending(true);
    try {
      const res = await client.post("/admin/reels-digest/send");
      if (res.data?.status === "sent") {
        toast.success(`Digest emailed to ${res.data.to_email}`);
      } else if (res.data?.status === "skipped_no_data") {
        toast("No reel clicks yet — nothing to rank", { icon: "ℹ" });
      } else {
        toast.error(res.data?.error || "Send failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const shade = (count, max) => {
    if (!count) return "bg-white/[0.05]";
    const r = count / Math.max(1, max);
    if (r > 0.66) return "bg-emerald-400";
    if (r > 0.33) return "bg-emerald-500/70";
    return "bg-emerald-600/40";
  };

  // Build week columns (Mon-start), padding the first partial week
  let weeks = [];
  if (data?.buckets?.length) {
    let col = new Array(data.buckets[0].weekday).fill(null);
    for (const b of data.buckets) {
      col.push(b);
      if (col.length === 7) {
        weeks.push(col);
        col = [];
      }
    }
    if (col.length) weeks.push([...col, ...new Array(7 - col.length).fill(null)]);
  }
  const dayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const maxWeekday = Math.max(1, ...(data?.weekday_totals || []).map((w) => w.total));

  return (
    <div
      className="mt-4 rounded-lg border border-sky-500/25 bg-black/40 p-3"
      data-testid="admin-dnp-reel-heatmap"
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-sky-300 font-bold mb-0.5">
            🗓 30-Day Click Heatmap
          </div>
          <div className="text-[12px] text-white/60">
            {data ? (
              <>
                <span className="font-mono text-sky-300" data-testid="admin-dnp-reel-heatmap-total">
                  {data.total_in_window} clicks
                </span>{" "}
                in the last 30 days
                {data.best_weekday && (
                  <span className="ml-1.5">
                    · best day:{" "}
                    <b className="text-[#FF9764]" data-testid="admin-dnp-reel-heatmap-best-day">
                      {data.best_weekday}
                    </b>
                  </span>
                )}
              </>
            ) : (
              "Loading…"
            )}
          </div>
        </div>
        <div className="shrink-0 flex flex-col items-end gap-1">
          <button
            type="button"
            onClick={sendDigest}
            disabled={sending}
            data-testid="admin-dnp-reels-digest-send"
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-bold bg-white/10 hover:bg-white/20 text-white border border-white/10 transition-colors disabled:opacity-50"
            title="Email the weekly top-3 reels digest to your inbox right now"
          >
            <PaperPlaneTilt size={10} weight="bold" />
            {sending ? "Sending…" : "Email me this report"}
          </button>
          {lastDigest && (
            <div className="text-[9px] text-white/40 font-mono" data-testid="admin-dnp-digest-open-status">
              Last digest:{" "}
              {lastDigest.opened_at
                ? `opened ✓${(lastDigest.open_count || 1) > 1 ? ` ×${lastDigest.open_count}` : ""}`
                : "sent — not opened yet"}
            </div>
          )}
        </div>
      </div>

      {err && (
        <div className="text-[11px] text-red-300 font-mono" data-testid="admin-dnp-reel-heatmap-error">
          {err}
        </div>
      )}

      {data && !err && (
        <div className="flex flex-wrap items-start gap-5">
          {/* Calendar grid */}
          <div className="flex gap-1" data-testid="admin-dnp-reel-heatmap-grid">
            <div className="flex flex-col gap-1 mr-0.5">
              {dayLabels.map((d, i) => (
                <div key={d} className="h-4 flex items-center text-[8px] text-white/35 font-mono w-6">
                  {i % 2 === 0 ? d : ""}
                </div>
              ))}
            </div>
            {weeks.map((col, wi) => (
              <div key={wi} className="flex flex-col gap-1">
                {col.map((b, di) =>
                  b ? (
                    <div
                      key={b.date}
                      data-testid={`admin-dnp-reel-heatmap-cell-${b.date}`}
                      title={`${b.date} · ${b.count} click${b.count === 1 ? "" : "s"}`}
                      className={`w-4 h-4 rounded-[3px] ${shade(b.count, data.max_daily_count)} transition-colors hover:ring-1 hover:ring-white/40`}
                    />
                  ) : (
                    <div key={`pad-${wi}-${di}`} className="w-4 h-4" />
                  )
                )}
              </div>
            ))}
          </div>

          {/* Weekday totals bars */}
          <div className="flex-1 min-w-[140px] space-y-1" data-testid="admin-dnp-reel-heatmap-weekdays">
            {(data.weekday_totals || []).map((w) => (
              <div key={w.label} className="flex items-center gap-2 text-[10px] font-mono">
                <span className={`w-7 ${w.label === data.best_weekday ? "text-[#FF9764] font-bold" : "text-white/45"}`}>
                  {w.label}
                </span>
                <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${w.label === data.best_weekday ? "bg-[#FF5A1F]" : "bg-sky-500/60"}`}
                    style={{ width: `${(w.total / maxWeekday) * 100}%` }}
                  />
                </div>
                <span className="w-6 text-right text-white/55">{w.total}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

